(() => {
  'use strict';

  const SCRIPT_VERSION = '1.38.1';
  const A4_WIDTH_PT = 595.275590551;
  const A4_HEIGHT_PT = 841.88976378;
  const MM_TO_PT = 72 / 25.4;
  const MAX_FILE_BYTES = 75 * 1024 * 1024;
  const MAX_PAGES = 100;

  const COURIER_PROFILES = Object.freeze({
    evri: {
      id: 'evri',
      label: 'EVRi',
      uploadTitle: 'EVRi shipping-label PDF',
      uploadHelp: 'Choose the PDF containing the EVRi courier labels for these orders.',
      ocrCrop: { x: 0, y: 0.27, width: 0.76, height: 0.68 }
    },
    amazon_shipping: {
      id: 'amazon_shipping',
      label: 'Amazon Shipping',
      uploadTitle: 'Amazon Shipping label PDF',
      uploadHelp: 'Choose the PDF containing the Amazon Shipping courier labels for these orders. Matching uses the recipient name, postcode and address when the Amazon order ID is not printed on the label.',
      ocrCrop: null
    },
    royal_mail: {
      id: 'royal_mail',
      label: 'Royal Mail Click & Drop',
      uploadTitle: 'Royal Mail Click & Drop label PDF',
      uploadHelp: 'Choose the Click & Drop PDF containing the Royal Mail shipping labels for these orders.',
      ocrCrop: null
    },
    fedex: {
      id: 'fedex',
      label: 'FedEx',
      uploadTitle: 'FedEx shipping-label PDF',
      uploadHelp: 'Choose the PDF containing the FedEx shipping labels for these orders.',
      ocrCrop: null
    },
    apc: {
      id: 'apc',
      label: 'APC Overnight',
      uploadTitle: 'APC Overnight shipping-label PDF',
      uploadHelp: 'Choose the PDF containing the APC Overnight shipping labels for these orders.',
      ocrCrop: null
    }
  });

  const SALES_CHANNEL_PROFILES = Object.freeze({
    amazon: {
      id: 'amazon',
      label: 'Amazon',
      packingTitle: 'Amazon packing-slip PDF',
      packingHelp: 'Choose the corresponding Amazon packing-slip PDF. Standard Amazon Marketplace slips, compact Qty/Product slips and newer Items/order-summary slips are supported. Order ID and address matching are used first; page order is the final fallback when enabled.'
    },
    tiktok: {
      id: 'tiktok',
      label: 'TikTok Shop',
      packingTitle: 'TikTok Shop packing-slip PDF',
      packingHelp: 'Choose the TikTok packing-slip PDF in the same page order as the courier-label PDF. TikTok pages are matched by page position.'
    },
    temu: {
      id: 'temu',
      label: 'Temu',
      packingTitle: 'Temu packing-slip / order PDF',
      packingHelp: 'Choose the corresponding Temu packing-slip PDF. Tracking number is used first; page order is the fallback when enabled.'
    },
    ebay: {
      id: 'ebay',
      label: 'eBay',
      packingTitle: 'eBay order / packing-slip PDF',
      packingHelp: 'Choose the corresponding eBay order or packing-slip PDF. Order references are used first; page order is the final fallback when enabled.'
    },
    shopify: {
      id: 'shopify',
      label: 'Shopify',
      packingTitle: 'Shopify packing-slip PDF',
      packingHelp: 'Choose the corresponding Shopify packing-slip PDF. Order references are used first; page order is the final fallback when enabled.'
    }
  });

  const PAPER_PROFILES = Object.freeze({
    top_left_100x150: {
      id: 'top_left_100x150',
      label: '100 × 150 mm — top left',
      shortLabel: '100 × 150 mm top-left',
      description: 'Peel-off label 10 mm from the top and left, with the privacy-safe packing summary below.',
      autoRotateLabel: false,
      outputMode: 'integrated_a4',
      pageWidthMm: 210,
      pageHeightMm: 297,
      layout: {
        labelX: 10,
        labelY: 10,
        labelWidth: 100,
        labelHeight: 150,
        packingX: 10,
        packingY: 165,
        packingWidth: 190,
        packingHeight: 122
      }
    },
    bottom_160x105: {
      id: 'bottom_160x105',
      label: '160 × 105 mm — bottom centre',
      shortLabel: '160 × 105 mm bottom-centre',
      description: 'Click & Drop-style sheet with the packing summary above and the peel-off label centred 8 mm from the bottom.',
      autoRotateLabel: true,
      outputMode: 'integrated_a4',
      pageWidthMm: 210,
      pageHeightMm: 297,
      layout: {
        labelX: 25,
        labelY: 184,
        labelWidth: 160,
        labelHeight: 105,
        packingX: 10,
        packingY: 10,
        packingWidth: 190,
        packingHeight: 154
      }
    },
    thermal_6x4: {
      id: 'thermal_6x4',
      label: '6 × 4 in thermal — separate pages',
      shortLabel: '6 × 4 in thermal',
      description: 'Zebra/thermal layout: each order prints as a 4 × 6 in shipping-label page followed by a 6 × 4 landscape privacy-safe packing-slip page with larger text. Long orders automatically continue onto extra 6 × 4 packing pages.',
      autoRotateLabel: true,
      outputMode: 'thermal_separate',
      pageWidthMm: 101.6,
      pageHeightMm: 152.4,
      layout: {
        labelX: 0,
        labelY: 0,
        labelWidth: 101.6,
        labelHeight: 152.4,
        packingX: 4,
        packingY: 4,
        packingWidth: 93.6,
        packingHeight: 144.4
      }
    }
  });

  const CDN = {
    pdfjs: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js',
    pdfWorker: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js',
    pdfLib: 'https://unpkg.com/pdf-lib@1.17.1/dist/pdf-lib.min.js',
    tesseract: 'https://unpkg.com/tesseract.js@6.0.1/dist/tesseract.min.js'
  };

  const scriptPromises = new Map();

  function loadScript(src, globalName) {
    if (globalName && window[globalName]) return Promise.resolve(window[globalName]);
    if (scriptPromises.has(src)) return scriptPromises.get(src);

    const promise = new Promise((resolve, reject) => {
      const existing = [...document.scripts].find((script) => script.src === src);
      const script = existing || document.createElement('script');
      const finish = () => {
        if (globalName && !window[globalName]) {
          reject(new Error(`Loaded ${src}, but ${globalName} was not available.`));
          return;
        }
        resolve(globalName ? window[globalName] : true);
      };
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => reject(new Error(`Could not load ${src}`)), { once: true });
      if (!existing) {
        script.src = src;
        script.async = true;
        script.crossOrigin = 'anonymous';
        document.head.appendChild(script);
      } else if (globalName && window[globalName]) {
        finish();
      }
    });

    scriptPromises.set(src, promise);
    return promise;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function normaliseWhitespace(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function normaliseSearchText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function chooseDetectedProfile(scores, minimumScore = 80, minimumMargin = 25) {
    const ranked = Object.entries(scores || {})
      .map(([id, score]) => ({ id, score: Number(score) || 0 }))
      .sort((a, b) => b.score - a.score);
    const best = ranked[0] || { id: '', score: 0 };
    const runnerUp = ranked[1] || { id: '', score: 0 };
    if (!best.id || best.score < minimumScore) return null;
    if (runnerUp.score > 0 && (best.score - runnerUp.score) < minimumMargin && best.score < 150) return null;
    return {
      id: best.id,
      score: best.score,
      margin: best.score - runnerUp.score,
      confidence: best.score >= 150 || (best.score >= 110 && (best.score - runnerUp.score) >= 40) ? 'high' : 'medium'
    };
  }

  function detectCourierFromText(text) {
    const source = String(text || '').toUpperCase();
    const scores = { evri: 0, amazon_shipping: 0, royal_mail: 0, fedex: 0, apc: 0 };

    if (/\bEVRI\b/.test(source)) scores.evri += 160;
    // OCR can render the stylised final 'i' in EVRi as 1, I, L or |.
    if (/\bEVR[1IL|]\b/.test(source)) scores.evri += 130;
    if (/H[\s-]*0?55B[\s-]*A[\s-]*(?:[A-Z0-9][\s-]*){8,14}/.test(source)) scores.evri += 100;
    // Marketplace EVRi PDFs are often image-only. Even when OCR misses the EVRi
    // logo, the routing block is distinctive: NDAY, VAN, DROP, C-ROUND and SKU.
    if (/\bC\s*[- ]?\s*ROUND\b/.test(source)) scores.evri += 35;
    const hasEvriRoute = /\bVAN\s*\d{1,3}\b/.test(source) && /\bDROP\s*\d{1,3}\b/.test(source);
    if (hasEvriRoute) scores.evri += 35;
    if (/\bNDAY\b/.test(source) && hasEvriRoute) scores.evri += 45;
    if (/\bMARKETPLACE\b/.test(source) && /\bDESTINATION\b/.test(source) && /\bSKU\b/.test(source) && hasEvriRoute) scores.evri += 70;
    if (/\bT0?5PHA[A-Z0-9]{7,14}\b/.test(source)) scores.evri += 90;
    if (/TEMU[-\s]*FULFIL+MENT/.test(source)) scores.evri += 25;

    if (/AMAZON\s+SHIPPING/.test(source)) scores.amazon_shipping += 180;
    if (/IF\s+UNDELIVERABLE/.test(source)) scores.amazon_shipping += 90;
    if (/PRINT\s+RETURN\s+LABEL/.test(source)) scores.amazon_shipping += 80;
    if (/\bCYCLE\s*[12]\b/.test(source)) scores.amazon_shipping += 55;
    if (/\bUK\s*\d{8,14}\b/.test(source)) scores.amazon_shipping += 20;

    if (/ROYAL\s+MAIL/.test(source)) scores.royal_mail += 180;
    if (/CLICK\s*(?:&|AND)\s*DROP/.test(source)) scores.royal_mail += 120;
    if (/TRACKED\s*(?:24|48)/.test(source)) scores.royal_mail += 60;
    if (/SPECIAL\s+DELIVERY/.test(source)) scores.royal_mail += 60;

    if (/\bFEDEX\b/.test(source)) scores.fedex += 180;
    if (/FEDEX\s+EXPRESS/.test(source)) scores.fedex += 60;
    if (/\bTRK#/.test(source) || /MASTER\s+TRACKING/.test(source)) scores.fedex += 40;

    if (/APC\s+OVERNIGHT(?:\s+LOGISTICS)?/.test(source)) scores.apc += 200;
    if (/\bAPC\b/.test(source) && /\bCONSIGNMENT\b/.test(source)) scores.apc += 100;

    return chooseDetectedProfile(scores);
  }

  function detectSalesChannelFromText(text) {
    const source = String(text || '').toUpperCase();
    const scores = { amazon: 0, tiktok: 0, temu: 0, ebay: 0, shopify: 0 };

    if (/AMAZON\.CO\.UK/.test(source)) scores.amazon += 180;
    if (/\bASIN\b/.test(source)) scores.amazon += 70;
    if (/ORDER\s+ITEM\s+ID/.test(source)) scores.amazon += 70;
    if (/LOVE\s+YOUR\s+PURCHASE/.test(source)) scores.amazon += 100;
    if (/\bSOLD\s+BY\b/.test(source) && /\bSHIPPING\s+TO\b/.test(source)) scores.amazon += 100;
    if (/\b[0-9OQIL|]{3}\s*[-–—]\s*[0-9OQIL|]{7}\s*[-–—]\s*(?:[0-9OQIL|]\s*){7}\b/.test(source)) scores.amazon += 65;
    if (/UNIT\s+PRICE/.test(source) && /ORDER\s+TOTALS?/.test(source)) scores.amazon += 45;

    if (/TIKTOK\s+SHOP/.test(source)) scores.tiktok += 200;
    else if (/\bTIKTOK\b/.test(source)) scores.tiktok += 160;
    if (/SELLER\s+SKU/.test(source)) scores.tiktok += 45;
    if (/PACKAGE\s+ID/.test(source)) scores.tiktok += 25;

    if (/\bTEMU\b/.test(source)) scores.temu += 120;
    if (/PURCHASE\s+ORDER\s+ID/.test(source)) scores.temu += 200;
    if (/GOODS\s+SKU\s+ID/.test(source)) scores.temu += 100;
    if (/CONTRIBUTION\s+SKU/.test(source)) scores.temu += 100;
    if (/QTY\s+ORDERED/.test(source)) scores.temu += 80;
    if (/TRACKING\s+NUMBER/.test(source) && /\bPO[-\s][A-Z0-9-]{8,}/.test(source)) scores.temu += 80;

    if (/\bEBAY(?:\.CO\.UK)?\b/.test(source)) scores.ebay += 200;
    if (/\b\d{2}-\d{5}-\d{5}\b/.test(source)) scores.ebay += 100;
    if (/ITEM\s+(?:NUMBER|ID)/.test(source) && /EBAY/.test(source)) scores.ebay += 40;

    if (/\bSHOPIFY\b/.test(source)) scores.shopify += 200;
    if (/\bSHIP\s+TO\b/.test(source) && /\bBILL\s+TO\b/.test(source)) scores.shopify += 130;
    if (/\bITEMS\b/.test(source) && /\bQUANTITY\b/.test(source)) scores.shopify += 70;
    if (/\bORDER\s*#\s*\d{3,12}\b/.test(source)) scores.shopify += 45;
    if (/THANK\s+YOU\s+FOR\s+SHOPPING\s+WITH\s+US/.test(source)) scores.shopify += 50;

    return chooseDetectedProfile(scores);
  }

  function normaliseOrderId(value) {
    const digits = String(value || '')
      .toUpperCase()
      .replace(/[OQ]/g, '0')
      .replace(/[IL|]/g, '1')
      .replace(/[^0-9]/g, '');
    if (digits.length === 17) return `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10)}`;
    return digits;
  }

  function normalisePostcode(value) {
    const compact = String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9|]/g, '');

    if (/^GIR[0O]AA$/.test(compact)) return 'GIR 0AA';

    // Tolerate common OCR mistakes in the numeric positions of UK postcodes.
    // Example: RG27 OEL -> RG27 0EL.
    const match = /^([A-Z]{1,2})([0-9OQIL|])([A-Z0-9OQIL|]?)([0-9OQIL|])([A-Z]{2})$/.exec(compact);
    if (!match) return '';

    const digit = (character) => String(character || '')
      .replace(/[OQ]/g, '0')
      .replace(/[IL|]/g, '1');
    const outwardThird = match[3]
      ? (/^[0-9OQIL|]$/.test(match[3]) ? digit(match[3]) : match[3])
      : '';
    const normalised = `${match[1]}${digit(match[2])}${outwardThird}${digit(match[4])}${match[5]}`;
    return `${normalised.slice(0, -3)} ${normalised.slice(-3)}`;
  }

  function extractOrderIds(text) {
    const source = String(text || '').toUpperCase();
    const results = new Set();
    // Amazon can render the last seven digits with an internal space on some
    // compact packing slips (for example 205-6904209-914 1120). Treat that as
    // the normal 3-7-7 Amazon order number rather than losing the identifier.
    const tolerantAmazon = /([0-9OQIL|]{3})\s*[-–—]\s*([0-9OQIL|]{7})\s*[-–—]\s*((?:[0-9OQIL|]\s*){7})/g;
    for (const match of source.matchAll(tolerantAmazon)) {
      const normalised = normaliseOrderId(match[0]);
      if (normalised) results.add(normalised);
    }
    for (const match of source.matchAll(/\b\d{17}\b/g)) {
      results.add(normaliseOrderId(match[0]));
    }
    for (const match of source.matchAll(/\b\d{18}\b/g)) {
      results.add(match[0]);
    }
    return [...results];
  }


  function extractNoisyAmazonOrderIds(text) {
    const source = String(text || '')
      .toUpperCase()
      .replace(/[OQ]/g, '0')
      .replace(/[IL|]/g, '1');
    const results = new Set();

    // Image-only courier labels often OCR the printed Amazon order reference
    // without its hyphens, for example "206 8670424 4193138". Accept the
    // canonical 3-7-7 digit grouping when the separators are only short runs of
    // punctuation/whitespace. This is deliberately limited to 17-digit Amazon
    // references so tracking/barcode numbers are not mistaken for order IDs.
    for (const match of source.matchAll(/(?:^|[^0-9])(\d{3})[^0-9]{0,5}(\d{7})[^0-9]{0,5}(\d{7})(?=$|[^0-9])/g)) {
      results.add(`${match[1]}-${match[2]}-${match[3]}`);
    }

    // The focused EVRi crop normally contains the small SKU row. OCR sometimes
    // inserts spaces between individual digits, so inspect SKU-adjacent lines
    // and recover a 17-digit sequence after removing non-digits.
    const lines = source.split(/\r?\n/).map((line) => normaliseWhitespace(line)).filter(Boolean);
    for (let index = 0; index < lines.length; index += 1) {
      if (!/SKU|ORDER/.test(lines[index])) continue;
      const windowText = lines.slice(index, index + 3).join(' ');
      const digits = windowText.replace(/[^0-9]/g, '');
      if (digits.length === 17) results.add(normaliseOrderId(digits));
    }

    return [...results].map(normaliseOrderId).filter(Boolean);
  }

  function amazonOrderDigits(value) {
    const normalised = normaliseOrderId(value);
    const digits = String(normalised || '').replace(/[^0-9]/g, '');
    return digits.length === 17 ? digits : '';
  }

  function findNearAmazonOrderMatch(shippingOrderIds, packingOrderIds) {
    let best = null;
    for (const shippingId of shippingOrderIds || []) {
      const shippingDigits = amazonOrderDigits(shippingId);
      if (!shippingDigits) continue;
      for (const packingId of packingOrderIds || []) {
        const packingDigits = amazonOrderDigits(packingId);
        if (!packingDigits || shippingDigits === packingDigits) continue;
        const distance = levenshtein(shippingDigits, packingDigits);
        if (distance !== 1) continue;
        best = {
          shippingId: normaliseOrderId(shippingId),
          packingId: normaliseOrderId(packingId),
          distance
        };
        return best;
      }
    }
    return best;
  }


  function reconcileAmazonEvriOcrOrderIds(shippingRecords, packingRecords) {
    const shipping = Array.isArray(shippingRecords) ? shippingRecords : [];
    const packing = Array.isArray(packingRecords) ? packingRecords : [];
    if (!shipping.length || !packing.length) return 0;

    const packingOccurrences = new Map();
    for (const record of packing) {
      for (const rawId of record?.orderIds || []) {
        const id = normaliseOrderId(rawId);
        if (!amazonOrderDigits(id)) continue;
        if (!packingOccurrences.has(id)) packingOccurrences.set(id, []);
        packingOccurrences.get(id).push(record);
      }
    }

    let corrected = 0;

    for (const shippingRecord of shipping) {
      if (shippingRecord?.salesChannel !== 'amazon' || shippingRecord?.courierId !== 'evri') continue;

      const currentIds = [...new Set((shippingRecord.orderIds || []).map(normaliseOrderId).filter(Boolean))];
      if (!currentIds.length) continue;

      // If OCR already produced an exact packing-slip order ID, leave it alone.
      const alreadyExact = currentIds.some((id) => packingOccurrences.has(id));
      if (alreadyExact) continue;

      const candidates = [];
      for (const shippingId of currentIds) {
        const shippingDigits = amazonOrderDigits(shippingId);
        if (!shippingDigits) continue;

        for (const [packingId, records] of packingOccurrences.entries()) {
          // Do not auto-correct against an ambiguous split-shipment packing ID.
          if (records.length !== 1) continue;
          const packingDigits = amazonOrderDigits(packingId);
          if (!packingDigits || levenshtein(shippingDigits, packingDigits) !== 1) continue;

          const packingRecord = records[0];
          const postcodeExact = (shippingRecord.postcodes || []).some((postcode) =>
            (packingRecord.postcodes || []).includes(postcode)
          );
          const nameScore = Math.max(
            similarity(shippingRecord.name, packingRecord.name),
            tokenSimilarity(shippingRecord.name, packingRecord.name)
          );
          const addressScore = tokenSimilarity(shippingRecord.address, packingRecord.address);

          // A 17-digit Amazon order reference with only one OCR digit wrong is
          // already highly distinctive. Require one independent recipient signal
          // before correcting it to the exact packing-slip ID.
          const corroborated = postcodeExact || nameScore >= 0.90 || addressScore >= 0.72;
          if (!corroborated) continue;

          candidates.push({
            shippingId,
            packingId,
            packingRecord,
            postcodeExact,
            nameScore,
            addressScore
          });
        }
      }

      const uniquePackingIds = [...new Set(candidates.map((candidate) => candidate.packingId))];
      if (uniquePackingIds.length !== 1) continue;

      const correctedId = uniquePackingIds[0];
      shippingRecord.orderIds = [
        correctedId,
        ...currentIds.filter((id) => {
          const digits = amazonOrderDigits(id);
          const correctedDigits = amazonOrderDigits(correctedId);
          return !(digits && correctedDigits && levenshtein(digits, correctedDigits) <= 1);
        })
      ];
      shippingRecord.orderIds = [...new Set(shippingRecord.orderIds.map(normaliseOrderId).filter(Boolean))];
      corrected += 1;
    }

    return corrected;
  }

  function normaliseTemuOrderId(value) {
    const compact = String(value || '')
      .toUpperCase()
      .replace(/\s+/g, '')
      .replace(/[^A-Z0-9]/g, '');
    return /^PO[A-Z0-9]{6,35}$/.test(compact) ? compact : '';
  }

  function normaliseTrackingId(value) {
    const compact = String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');

    // EVRi/Temu tracking references in this workflow use the constrained
    // H055BA########## shape. OCR can confuse 0/O, 1/I/L, 5/S, 2/Z or 6/G.
    // Correct only the numeric positions of that known shape, leaving generic
    // tracking identifiers untouched. This keeps exact tracking matching safe
    // while making image-only Temu packing slips substantially more robust.
    if (compact.length === 16 && compact.startsWith('H')) {
      const chars = [...compact];
      const numericMap = { O: '0', Q: '0', I: '1', L: '1', S: '5', Z: '2', G: '6' };
      const numericPositions = [1, 2, 3, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];
      for (const index of numericPositions) {
        chars[index] = numericMap[chars[index]] || chars[index];
      }
      if (chars[4] === '8') chars[4] = 'B';
      if (chars[5] === '4') chars[5] = 'A';
      const evri = chars.join('');
      if (/^H\d{3}BA\d{10}$/.test(evri)) return evri;
    }

    return /^[A-Z0-9]{10,40}$/.test(compact) ? compact : '';
  }

  function extractTrackingIds(text, salesChannel = 'amazon') {
    const source = String(text || '').toUpperCase();
    const results = new Set();

    // EVRi labels may print the same tracking number with separators, for
    // example H-055B-A-004405631-5, while Temu prints H055BA0044056315.
    // Canonicalising to letters/digits only makes those values identical.
    for (const match of source.matchAll(/\bH(?:[\s\-]*[A-Z0-9]){15}\b/g)) {
      const value = normaliseTrackingId(match[0]);
      if (value) results.add(value);
    }

    // Marketplace packing documents normally show a labelled tracking field.
    // Read only the first long identifier after the label so trailing text such
    // as "Page 1 / 1 Pages" is not included.
    for (const line of source.split(/\r?\n/)) {
      const labelled = /(?:TRACKING(?:\s+(?:NUMBER|NO\.?|ID))?|WAYBILL|CONSIGNMENT(?:\s+(?:NUMBER|NO\.?|ID))?)\s*[:#-]?\s*([A-Z0-9][A-Z0-9\-]{9,39})/.exec(line);
      if (!labelled) continue;
      const value = normaliseTrackingId(labelled[1]);
      if (value) results.add(value);
    }

    return [...results];
  }

  function extractChannelOrderIds(text, salesChannel = 'amazon') {
    const source = String(text || '').toUpperCase();
    const results = new Set(extractOrderIds(source));

    if (salesChannel === 'temu') {
      // Temu customer order numbers normally begin with PO. The same order
      // reference can be printed with dots, dashes or spaces, so it is
      // canonicalised before matching against the courier label.
      for (const line of source.split(/\r?\n/)) {
        for (const match of line.matchAll(/\bPO(?:[.\-][ \t]*|[ \t]+)?[A-Z0-9][A-Z0-9.\-]{5,35}\b/g)) {
          const value = normaliseTemuOrderId(match[0]);
          if (value) results.add(value);
        }
      }
    }

    if (salesChannel === 'ebay') {
      for (const match of source.matchAll(/\b\d{2}-\d{5}-\d{5}\b/g)) results.add(match[0]);
    }

    if (salesChannel === 'shopify') {
      for (const match of source.matchAll(/(?:ORDER(?:\s+NUMBER|\s+NO\.?|\s+ID)?|REFERENCE|CUSTOMER\s+REF(?:ERENCE)?)\s*[:#-]?\s*#?([A-Z0-9-]{3,24})/g)) {
        const value = normaliseWhitespace(match[1]).replace(/^#/, '');
        if (value) results.add(value);
      }
      for (const match of source.matchAll(/#(\d{3,12})\b/g)) results.add(match[1]);
    }

    // Click & Drop and marketplace documents commonly print a labelled customer
    // reference even when the marketplace-specific order format is not recognised.
    for (const match of source.matchAll(/(?:ORDER(?:\s+ID|\s+NUMBER|\s+NO\.?|\s+REF(?:ERENCE)?)?|CUSTOMER\s+REF(?:ERENCE)?|CHANNEL\s+REF(?:ERENCE)?|SALES\s+REFERENCE)\s*[:#-]?\s*([A-Z0-9][A-Z0-9.\-]{4,40})/g)) {
      const value = normaliseWhitespace(match[1]).replace(/[.,;:]$/, '');
      if (value && !/^20\d{2}-\d{2}-\d{2}$/.test(value)) results.add(value);
    }

    return [...results];
  }

  function extractPostcodes(text) {
    const source = String(text || '')
      .toUpperCase()
      .replace(/\$/g, 'S');
    const regex = /\b(?:GIR\s?[0O]AA|[A-Z]{1,2}[0-9OQIL|][A-Z0-9OQIL|]?\s?[0-9OQIL|][A-Z]{2})\b/g;
    return [...new Set(
      [...source.matchAll(regex)]
        .filter((match) => /\d/.test(match[0]))
        .map((match) => normalisePostcode(match[0]))
        .filter(Boolean)
    )];
  }

  function extractAmazonShippingRecipient(lines) {
    const cleaned = (lines || []).map(normaliseWhitespace).filter(Boolean);
    if (!cleaned.length) return { name: '', address: '' };

    const postcodeIndex = cleaned.findIndex((line) => extractPostcodes(line).length > 0);
    if (postcodeIndex < 0) return { name: '', address: '' };

    const skip = /(?:undeliverable|print return label|\bkgs?\b|\bcycle\b|amazon\s*shipping|^uk\d+|^stn\d+|^\s*b\s*\d+|^\d{1,2}[\/.-]\d{1,2}(?:[\/.-]\d{2,4})?$)/i;
    let streetIndex = -1;
    for (let index = postcodeIndex - 1; index >= Math.max(0, postcodeIndex - 5); index -= 1) {
      const line = cleaned[index];
      if (skip.test(line)) continue;
      if (/^\d+[A-Z]?\b/i.test(line) || /\b(?:ROAD|STREET|CLOSE|AVENUE|LANE|DRIVE|GARDENS|WAY|COURT|CRESCENT|PLACE|TERRACE|PARK|GROVE|RISE|MEWS|HILL)\b/i.test(line)) {
        streetIndex = index;
        break;
      }
    }

    let nameIndex = -1;
    const nameSearchStart = streetIndex >= 0 ? streetIndex - 1 : postcodeIndex - 1;
    for (let index = nameSearchStart; index >= Math.max(0, postcodeIndex - 7); index -= 1) {
      const line = cleaned[index];
      if (skip.test(line)) continue;
      if (/\d{2,}/.test(line)) continue;
      if (!/[A-Z]{2}/i.test(line)) continue;
      nameIndex = index;
      break;
    }

    const name = nameIndex >= 0 ? cleaned[nameIndex] : '';
    const addressStart = nameIndex >= 0 ? nameIndex : (streetIndex >= 0 ? streetIndex : Math.max(0, postcodeIndex - 2));
    const address = cleaned.slice(addressStart, postcodeIndex + 1).join(' ');
    return { name, address };
  }

  function extractName(lines, type) {
    const markers = type === 'shipping'
      ? ['destination', 'shipping to', 'ship to', 'deliver to', 'recipient', 'delivery address']
      : ['dispatch to', 'shipping to', 'delivery address', 'shipping address', 'ship to', 'deliver to'];
    const forbidden = /^(date|weight|order date|delivery service|buyer name|seller name|product details|quantity|return to|sku)$/i;

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const lower = line.toLowerCase();
      const marker = markers.find((candidate) => lower.includes(candidate));
      if (!marker) continue;

      const tail = normaliseWhitespace(
        line
          .slice(lower.indexOf(marker) + marker.length)
          .replace(/^\s*[:\-]/, '')
          .split(/\b(?:VAN|DROP|C-?ROUND|NDAY)\b/i)[0]
      );
      if (tail && !forbidden.test(tail) && /[a-z]{2}/i.test(tail) && (tail.match(/\d/g) || []).length <= 1) return tail;

      for (const candidate of lines.slice(index + 1, index + 6)) {
        const cleaned = normaliseWhitespace(candidate.split(/\b(?:VAN|DROP|C-?ROUND|NDAY)\b/i)[0]);
        if (!cleaned || forbidden.test(cleaned)) continue;
        if ((cleaned.match(/\d/g) || []).length <= 1 && /[a-z]{2}/i.test(cleaned)) return cleaned;
      }
    }
    return '';
  }

  function extractAddress(lines, type) {
    const markers = type === 'shipping'
      ? ['destination', 'shipping to', 'ship to', 'deliver to', 'recipient', 'delivery address']
      : ['dispatch to', 'shipping to', 'delivery address', 'shipping address', 'ship to', 'deliver to'];
    const stop = /^(date|weight|order id|order date|delivery service|buyer name|seller name|product details|quantity|thank you|sku)/i;

    for (let index = 0; index < lines.length; index += 1) {
      if (!markers.some((marker) => lines[index].toLowerCase().includes(marker))) continue;
      const collected = [];
      for (const candidate of lines.slice(index + 1, index + 11)) {
        const cleaned = normaliseWhitespace(candidate);
        if (!cleaned) continue;
        if (stop.test(cleaned)) break;
        collected.push(cleaned);
      }
      return collected.join(' ');
    }
    return lines.slice(0, 12).join(' ');
  }

  function parseIdentity(text, pageIndex, type, options = {}) {
    const salesChannel = typeof options === 'string'
      ? options
      : (options.salesChannel || 'amazon');
    const courierId = typeof options === 'object' && options ? (options.courierId || '') : '';
    const lines = String(text || '')
      .split(/\r?\n/)
      .map(normaliseWhitespace)
      .filter(Boolean);
    const amazonShippingRecipient = type === 'shipping' && courierId === 'amazon_shipping'
      ? extractAmazonShippingRecipient(lines)
      : null;
    return {
      pageIndex,
      type,
      salesChannel,
      courierId,
      rawText: text,
      orderIds: extractChannelOrderIds(text, salesChannel),
      trackingIds: extractTrackingIds(text, salesChannel),
      postcodes: extractPostcodes(text),
      name: amazonShippingRecipient?.name || extractName(lines, type),
      address: amazonShippingRecipient?.address || extractAddress(lines, type)
    };
  }

  function looksLikeAmazonShippingCourierLabel(text) {
    const value = normaliseWhitespace(String(text || '')).toLowerCase();
    if (!value) return false;
    const strongMarkers = [
      'amazon shipping',
      'if undeliverable',
      'print return label',
      'cycle 1',
      'cycle 2'
    ];
    const markerHits = strongMarkers.filter((marker) => value.includes(marker)).length;
    const hasUkTracking = /uk\d{8,14}/i.test(value.replace(/\s+/g, ''));
    const hasProductTable = /(qty|quantity).{0,40}(product|product details|items)/i.test(value)
      || /(product|product details|items).{0,40}(qty|quantity)/i.test(value);
    return !hasProductTable && (markerHits >= 1 || hasUkTracking);
  }

  function displayIdentity(record) {
    const parts = [];
    if (record.salesChannel === 'temu' && record.trackingIds?.length) {
      parts.push(`Tracking ${record.trackingIds.join(', ')}`);
    }
    if (record.orderIds.length) parts.push(`Order ${record.orderIds.join(', ')}`);
    if (record.salesChannel !== 'temu' && record.trackingIds?.length) {
      parts.push(`Tracking ${record.trackingIds.join(', ')}`);
    }
    if (record.postcodes.length) parts.push(record.postcodes.join(', '));
    if (record.name) parts.push(record.name);
    if (!parts.length && record.salesChannel === 'tiktok') {
      return `Page ${record.pageIndex + 1} (TikTok page order)`;
    }
    return parts.length ? parts.join(' | ') : 'No identifier found';
  }

  function levenshtein(a, b) {
    const left = normaliseSearchText(a);
    const right = normaliseSearchText(b);
    if (!left) return right.length;
    if (!right) return left.length;
    const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
    for (let i = 1; i <= left.length; i += 1) {
      const current = [i];
      for (let j = 1; j <= right.length; j += 1) {
        current[j] = Math.min(
          current[j - 1] + 1,
          previous[j] + 1,
          previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
        );
      }
      for (let j = 0; j < current.length; j += 1) previous[j] = current[j];
    }
    return previous[right.length];
  }

  function similarity(a, b) {
    const left = normaliseSearchText(a);
    const right = normaliseSearchText(b);
    if (!left || !right) return 0;
    return 1 - (levenshtein(left, right) / Math.max(left.length, right.length));
  }

  function tokenSimilarity(a, b) {
    const left = new Set(normaliseSearchText(a).split(' ').filter(Boolean));
    const right = new Set(normaliseSearchText(b).split(' ').filter(Boolean));
    if (!left.size || !right.size) return 0;
    const intersection = [...left].filter((item) => right.has(item)).length;
    return (2 * intersection) / (left.size + right.size);
  }

  function scorePair(shipping, packing) {
    const isTikTok = shipping.salesChannel === 'tiktok' || packing.salesChannel === 'tiktok';
    const isTemu = shipping.salesChannel === 'temu' || packing.salesChannel === 'temu';
    const isAmazon = shipping.salesChannel === 'amazon' || packing.salesChannel === 'amazon';

    // TikTok packing slips in this workflow do not carry a reliable tracking
    // number or recipient address. They are deliberately matched by page order
    // in matchPages(), rather than by weak text similarity.
    if (isTikTok) {
      return { score: 0, method: 'TikTok page order' };
    }

    if (isTemu) {
      const shippingTracking = shipping.trackingIds || [];
      const packingTracking = packing.trackingIds || [];
      if (shippingTracking.some((id) => packingTracking.includes(id))) {
        return { score: 100, method: 'Tracking ID' };
      }
      if (shippingTracking.length && packingTracking.length) {
        return { score: 0, method: 'Tracking ID mismatch' };
      }
      return { score: 0, method: 'Tracking ID not found' };
    }

    if (shipping.orderIds.some((id) => packing.orderIds.includes(id))) {
      return { score: 100, method: 'Order ID' };
    }

    const postcodeExact = shipping.postcodes.some((postcode) => packing.postcodes.includes(postcode));
    const nameScore = Math.max(similarity(shipping.name, packing.name), tokenSimilarity(shipping.name, packing.name));
    const addressScore = tokenSimilarity(shipping.address, packing.address);
    const nearAmazonOrder = isAmazon ? findNearAmazonOrderMatch(shipping.orderIds, packing.orderIds) : null;
    if (nearAmazonOrder) {
      // A single OCR digit error is common on image-only EVRi marketplace labels.
      // Never accept it on the order number alone: require independent recipient
      // evidence so one OCR mistake cannot silently pair two unrelated orders.
      const strongName = nameScore >= 0.82;
      const strongAddress = addressScore >= 0.62;
      if (postcodeExact && strongName) {
        return { score: 94, method: 'Near Order ID + postcode + name' };
      }
      if (postcodeExact || strongName || strongAddress) {
        return { score: 78, method: 'Near Order ID + recipient check' };
      }
    }

    let score = 0;
    const methods = [];
    if (postcodeExact) {
      score += 58;
      methods.push('postcode');
    } else if (shipping.postcodes.length && packing.postcodes.length) {
      const postcodeScore = Math.max(...shipping.postcodes.flatMap((left) => packing.postcodes.map((right) => similarity(left, right))));
      if (postcodeScore >= 0.8) {
        score += postcodeScore * 38;
        methods.push('similar postcode');
      }
    }

    if (nameScore > 0) {
      score += nameScore * 27;
      if (nameScore >= 0.6) methods.push('name');
    }

    if (addressScore > 0) {
      score += addressScore * 15;
      if (addressScore >= 0.45) methods.push('address');
    }

    return { score: Math.min(99, score), method: methods.join(' + ') || 'weak text match' };
  }

  function confidenceFor(score, method) {
    if (method === 'Order ID' || method === 'Tracking ID' || score >= 84) return 'High';
    if (score >= 58) return 'Medium';
    return 'Low';
  }


  function duplicateIdentifierGroups(records, key) {
    const pagesByValue = new Map();
    for (const record of records || []) {
      for (const rawValue of record?.[key] || []) {
        const value = String(rawValue || '').trim();
        if (!value) continue;
        if (!pagesByValue.has(value)) pagesByValue.set(value, []);
        pagesByValue.get(value).push(Number(record.pageIndex));
      }
    }
    return [...pagesByValue.entries()]
      .filter(([, pages]) => pages.length > 1)
      .map(([value, pages]) => ({ value, pages: [...new Set(pages)].sort((a, b) => a - b) }))
      .filter((group) => group.pages.length > 1);
  }

  function duplicatePackingAssignments(matches) {
    const pages = new Map();
    for (const match of matches || []) {
      if (match?.excluded || match?.packingPage == null) continue;
      const packingPage = Number(match.packingPage);
      if (!pages.has(packingPage)) pages.set(packingPage, []);
      pages.get(packingPage).push(Number(match.shippingPage));
    }
    return [...pages.entries()]
      .filter(([, shippingPages]) => shippingPages.length > 1)
      .map(([packingPage, shippingPages]) => ({
        packingPage,
        shippingPages: [...new Set(shippingPages)].sort((a, b) => a - b)
      }))
      .filter((group) => group.shippingPages.length > 1);
  }

  function buildPreflightReport(shippingRecords, packingRecords, matches, packingSafety = {}) {
    const allMatches = Array.isArray(matches) ? matches : [];
    const includedMatches = allMatches.filter((match) => !match.excluded);
    const removedMatches = allMatches.filter((match) => match.excluded);
    const unmatchedIncluded = includedMatches.filter((match) => match.packingPage == null);
    const highIncluded = includedMatches.filter((match) => match.packingPage != null && match.confidence === 'High');
    const mediumIncluded = includedMatches.filter((match) => match.packingPage != null && match.confidence === 'Medium');
    const lowIncluded = includedMatches.filter((match) => match.packingPage != null && match.confidence === 'Low');

    const duplicateAssignments = duplicatePackingAssignments(allMatches);
    const unsafePackingPages = [];
    const pendingPackingPages = [];
    for (const match of includedMatches) {
      if (match.packingPage == null) continue;
      const page = Number(match.packingPage);
      const safety = packingSafety?.[page];
      if (safety?.safe === false) unsafePackingPages.push(page);
      else if (!safety || safety.safe !== true) pendingPackingPages.push(page);
    }

    const includedShippingPages = new Set(includedMatches.map((match) => Number(match.shippingPage)));
    const includedPackingPages = new Set(
      includedMatches
        .filter((match) => match.packingPage != null)
        .map((match) => Number(match.packingPage))
    );
    const includedShippingRecords = (shippingRecords || []).filter((record) => includedShippingPages.has(Number(record.pageIndex)));
    const includedPackingRecords = (packingRecords || []).filter((record) => includedPackingPages.has(Number(record.pageIndex)));

    const duplicateShippingTracking = duplicateIdentifierGroups(includedShippingRecords, 'trackingIds');
    const duplicatePackingTracking = duplicateIdentifierGroups(includedPackingRecords, 'trackingIds');
    const duplicateShippingOrders = duplicateIdentifierGroups(includedShippingRecords, 'orderIds');
    const duplicatePackingOrders = duplicateIdentifierGroups(includedPackingRecords, 'orderIds');

    const shippingWithoutStrongIdentifier = includedShippingRecords.filter((record) => (
      !(record?.trackingIds?.length) &&
      !(record?.orderIds?.length) &&
      !(record?.postcodes?.length) &&
      !normaliseWhitespace(record?.name)
    ));
    const packingWithoutStrongIdentifier = includedPackingRecords.filter((record) => (
      !(record?.trackingIds?.length) &&
      !(record?.orderIds?.length) &&
      !(record?.postcodes?.length) &&
      !normaliseWhitespace(record?.name)
    ));

    const blockers = [];
    const warnings = [];

    if (!includedMatches.length && allMatches.length) {
      blockers.push({
        code: 'no-included-orders',
        message: 'No orders are currently included in the batch.'
      });
    }
    if (unmatchedIncluded.length) {
      blockers.push({
        code: 'unmatched-orders',
        count: unmatchedIncluded.length,
        message: `${unmatchedIncluded.length} included order${unmatchedIncluded.length === 1 ? ' has' : 's have'} no packing-slip match.`
      });
    }
    if (duplicateAssignments.length) {
      blockers.push({
        code: 'duplicate-packing-assignment',
        count: duplicateAssignments.length,
        message: `${duplicateAssignments.length} packing-slip page${duplicateAssignments.length === 1 ? ' is' : 's are'} assigned to more than one included shipping label.`
      });
    }
    if (unsafePackingPages.length) {
      const unique = [...new Set(unsafePackingPages)];
      blockers.push({
        code: 'unsafe-packing-table',
        count: unique.length,
        pages: unique,
        message: `${unique.length} packing-slip page${unique.length === 1 ? '' : 's'} failed the privacy-safe product-table check. Remove or rematch ${unique.length === 1 ? 'that order' : 'those orders'} before generating.`
      });
    }
    if (pendingPackingPages.length) {
      const unique = [...new Set(pendingPackingPages)];
      blockers.push({
        code: 'pending-packing-check',
        count: unique.length,
        pages: unique,
        message: `Pre-flight product-table checking is still pending for ${unique.length} packing-slip page${unique.length === 1 ? '' : 's'}.`
      });
    }
    if (duplicateShippingTracking.length) {
      blockers.push({
        code: 'duplicate-shipping-tracking',
        count: duplicateShippingTracking.length,
        message: `${duplicateShippingTracking.length} duplicate shipping tracking identifier${duplicateShippingTracking.length === 1 ? ' was' : 's were'} detected in the label PDF.`
      });
    }
    if (duplicatePackingTracking.length) {
      warnings.push({
        code: 'duplicate-packing-tracking',
        count: duplicatePackingTracking.length,
        message: `${duplicatePackingTracking.length} duplicate tracking identifier${duplicatePackingTracking.length === 1 ? ' was' : 's were'} detected in the packing-slip PDF. Check the source file for duplicated pages.`
      });
    }
    if ((shippingRecords || []).length !== (packingRecords || []).length) {
      warnings.push({
        code: 'page-count-mismatch',
        message: `The PDFs contain different page counts: ${(shippingRecords || []).length} shipping-label page${(shippingRecords || []).length === 1 ? '' : 's'} and ${(packingRecords || []).length} packing-slip page${(packingRecords || []).length === 1 ? '' : 's'}.`
      });
    }
    if (mediumIncluded.length || lowIncluded.length) {
      const parts = [];
      if (mediumIncluded.length) parts.push(`${mediumIncluded.length} medium-confidence`);
      if (lowIncluded.length) parts.push(`${lowIncluded.length} low-confidence`);
      warnings.push({
        code: 'confidence-review',
        message: `${parts.join(' and ')} included match${mediumIncluded.length + lowIncluded.length === 1 ? '' : 'es'} should be reviewed before printing.`
      });
    }
    // Repeated order IDs are common for legitimate split shipments. They are
    // retained in the report for diagnostics, but do not create a customer-facing
    // warning by themselves. Genuine ambiguity is already caught by duplicate
    // packing assignments, duplicate tracking IDs and unmatched orders.
    if (shippingWithoutStrongIdentifier.length) {
      warnings.push({
        code: 'shipping-identifier-missing',
        count: shippingWithoutStrongIdentifier.length,
        message: `${shippingWithoutStrongIdentifier.length} shipping-label page${shippingWithoutStrongIdentifier.length === 1 ? '' : 's'} has no order ID, tracking ID, postcode or recipient name available for matching.`
      });
    }
    if (packingWithoutStrongIdentifier.length) {
      warnings.push({
        code: 'packing-identifier-missing',
        count: packingWithoutStrongIdentifier.length,
        message: `${packingWithoutStrongIdentifier.length} packing-slip page${packingWithoutStrongIdentifier.length === 1 ? '' : 's'} has no order ID, tracking ID, postcode or recipient name available for matching.`
      });
    }

    const reviewCount = mediumIncluded.length + lowIncluded.length + unmatchedIncluded.length;
    return {
      shippingCount: (shippingRecords || []).length,
      packingCount: (packingRecords || []).length,
      totalMatches: allMatches.length,
      includedCount: includedMatches.length,
      removedCount: removedMatches.length,
      highCount: highIncluded.length,
      mediumCount: mediumIncluded.length,
      lowCount: lowIncluded.length,
      reviewCount,
      unmatchedCount: unmatchedIncluded.length,
      duplicateAssignments,
      duplicateShippingTracking,
      duplicatePackingTracking,
      duplicateShippingOrders,
      duplicatePackingOrders,
      unsafePackingPages: [...new Set(unsafePackingPages)],
      pendingPackingPages: [...new Set(pendingPackingPages)],
      blockers,
      warnings,
      ready: blockers.length === 0 && includedMatches.length > 0
    };
  }

  function matchPages(shippingRecords, packingRecords, usePageOrder) {
    const usedShipping = new Set();
    const usedPacking = new Set();
    const results = new Map();

    const setResult = (candidate) => {
      usedShipping.add(candidate.shippingPage);
      if (candidate.packingPage != null) usedPacking.add(candidate.packingPage);
      results.set(candidate.shippingPage, {
        ...candidate,
        confidence: confidenceFor(candidate.score, candidate.method)
      });
    };

    // TEMU SAFETY PASS
    // Temu packing-slip PDFs are not guaranteed to be in the same page order as
    // the courier-label PDF. Lock every unique exact tracking-number match across
    // the whole batch BEFORE considering any page-order fallback. This prevents a
    // same-page fallback from stealing a packing page that belongs to a different
    // label later in the document.
    const packingPagesByTracking = new Map();
    packingRecords.forEach((packing) => {
      if (packing.salesChannel !== 'temu') return;
      for (const id of packing.trackingIds || []) {
        if (!packingPagesByTracking.has(id)) packingPagesByTracking.set(id, new Set());
        packingPagesByTracking.get(id).add(packing.pageIndex);
      }
    });

    shippingRecords.forEach((shipping) => {
      if (shipping.salesChannel !== 'temu') return;
      const pageCandidates = new Set();
      for (const id of shipping.trackingIds || []) {
        const pages = packingPagesByTracking.get(id);
        if (!pages) continue;
        pages.forEach((pageIndex) => pageCandidates.add(pageIndex));
      }
      const available = [...pageCandidates].filter((pageIndex) => !usedPacking.has(pageIndex));
      if (available.length === 1) {
        setResult({
          shippingPage: shipping.pageIndex,
          packingPage: available[0],
          score: 100,
          method: 'Tracking ID'
        });
      }
    });

    // Normal global matching pass for all remaining pages. Exact identifiers and
    // genuinely confident recipient matches are accepted before fallback.
    const candidates = [];
    shippingRecords.forEach((shipping) => {
      if (usedShipping.has(shipping.pageIndex)) return;
      packingRecords.forEach((packing) => {
        if (usedPacking.has(packing.pageIndex)) return;
        const result = scorePair(shipping, packing);
        candidates.push({ shippingPage: shipping.pageIndex, packingPage: packing.pageIndex, ...result });
      });
    });
    candidates.sort((a, b) => b.score - a.score);

    for (const candidate of candidates) {
      if (usedShipping.has(candidate.shippingPage) || usedPacking.has(candidate.packingPage)) continue;

      // Only accept exact identifiers or a genuinely confident text/address match
      // before using page order. This prevents a weak fuzzy match from stealing a
      // page that should be paired by position as the final backstop.
      const exactIdentifier = candidate.method === 'Order ID' || candidate.method === 'Tracking ID';
      if (!exactIdentifier && candidate.score < 58) continue;

      setResult(candidate);
    }

    for (const shipping of shippingRecords) {
      if (results.has(shipping.pageIndex)) continue;
      const sameIndex = packingRecords.find((packing) => packing.pageIndex === shipping.pageIndex && !usedPacking.has(packing.pageIndex));
      const isTikTok = shipping.salesChannel === 'tiktok';
      const isTemu = shipping.salesChannel === 'temu';
      const salesChannel = shipping.salesChannel || 'amazon';
      const channelLabel = SALES_CHANNEL_PROFILES[salesChannel]?.label || salesChannel;
      const shouldUsePageOrder = isTikTok || usePageOrder;

      if (shouldUsePageOrder && sameIndex) {
        // Critical Temu safeguard: when BOTH documents contain readable tracking
        // numbers and those numbers disagree, page order is known to be wrong.
        // Never pair them just because they share a page number. Leave the row
        // unmatched for review instead.
        if (isTemu) {
          const shippingTracking = shipping.trackingIds || [];
          const packingTracking = sameIndex.trackingIds || [];
          const exactTracking = shippingTracking.some((id) => packingTracking.includes(id));
          if (shippingTracking.length && packingTracking.length && !exactTracking) {
            results.set(shipping.pageIndex, {
              shippingPage: shipping.pageIndex,
              packingPage: null,
              score: 0,
              method: 'Tracking mismatch — review required',
              confidence: 'Low'
            });
            continue;
          }
        }

        usedPacking.add(sameIndex.pageIndex);
        results.set(shipping.pageIndex, {
          shippingPage: shipping.pageIndex,
          packingPage: sameIndex.pageIndex,
          score: isTikTok ? 55 : 40,
          method: isTikTok
            ? 'Page order (TikTok)'
            : `Page order fallback (${channelLabel})`,
          confidence: isTikTok ? 'Medium' : 'Low'
        });
      } else {
        results.set(shipping.pageIndex, {
          shippingPage: shipping.pageIndex,
          packingPage: null,
          score: 0,
          method: 'Unmatched',
          confidence: 'Low'
        });
      }
    }

    return [...results.values()].sort((a, b) => a.shippingPage - b.shippingPage);
  }

  async function extractPdfText(page) {
    const content = await page.getTextContent({ normalizeWhitespace: true });
    const rows = [];
    for (const item of content.items) {
      if (!item.str || !normaliseWhitespace(item.str)) continue;
      const x = item.transform?.[4] ?? 0;
      const y = item.transform?.[5] ?? 0;
      let row = rows.find((candidate) => Math.abs(candidate.y - y) <= 3.5);
      if (!row) {
        row = { y, items: [] };
        rows.push(row);
      }
      row.items.push({ x, text: item.str });
    }
    rows.sort((a, b) => b.y - a.y);
    return rows
      .map((row) => row.items.sort((a, b) => a.x - b.x).map((item) => item.text).join(' '))
      .join('\n');
  }

  async function renderPdfPage(pdf, pageIndex, targetWidth = 1450, maxScale = 3.2) {
    const page = await pdf.getPage(pageIndex + 1);
    const unitViewport = page.getViewport({ scale: 1 });
    const scale = clamp(targetWidth / unitViewport.width, 1.3, maxScale);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d', { alpha: false, willReadFrequently: true });
    context.fillStyle = '#fff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport, intent: 'print' }).promise;
    return canvas;
  }

  function cropCanvas(source, x, y, width, height) {
    const output = document.createElement('canvas');
    output.width = Math.max(1, Math.round(width));
    output.height = Math.max(1, Math.round(height));
    output.getContext('2d', { alpha: false }).drawImage(
      source,
      Math.round(x), Math.round(y), Math.round(width), Math.round(height),
      0, 0, output.width, output.height
    );
    return output;
  }

  function shouldRotateForBox(sourceWidth, sourceHeight, boxWidth, boxHeight) {
    if (![sourceWidth, sourceHeight, boxWidth, boxHeight].every((value) => Number.isFinite(value) && value > 0)) return false;
    const normalScale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
    const rotatedScale = Math.min(boxWidth / sourceHeight, boxHeight / sourceWidth);
    return rotatedScale > normalScale * 1.03;
  }

  function rotateCanvasClockwise(source) {
    const output = document.createElement('canvas');
    output.width = source.height;
    output.height = source.width;
    const context = output.getContext('2d', { alpha: false });
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, output.width, output.height);
    context.translate(output.width, 0);
    context.rotate(Math.PI / 2);
    context.drawImage(source, 0, 0);
    return output;
  }

  function orientShippingCanvasForPaper(source, layout, paperProfile) {
    if (!paperProfile?.autoRotateLabel) return source;
    return shouldRotateForBox(
      source.width,
      source.height,
      layout.labelWidth,
      layout.labelHeight
    ) ? rotateCanvasClockwise(source) : source;
  }

  function canvasBoundsToPdfBoundingBox(bounds, canvas, sourcePage) {
    const size = sourcePage.getSize();
    const pageWidth = size.width;
    const pageHeight = size.height;
    const left = (bounds.x / canvas.width) * pageWidth;
    const right = ((bounds.x + bounds.width) / canvas.width) * pageWidth;
    const top = pageHeight - ((bounds.y / canvas.height) * pageHeight);
    const bottom = pageHeight - (((bounds.y + bounds.height) / canvas.height) * pageHeight);
    return {
      left: clamp(left, 0, pageWidth),
      bottom: clamp(bottom, 0, pageHeight),
      right: clamp(right, 0, pageWidth),
      top: clamp(top, 0, pageHeight)
    };
  }

  async function drawShippingPageVector({ output, outputPage, sourcePdf, sourcePageIndex, sourceCanvas, bounds, layout, paperProfile }) {
    if (!sourcePdf) throw new Error('Source shipping PDF is unavailable for vector embedding.');
    const sourcePage = sourcePdf.getPage(sourcePageIndex);
    const rotation = ((sourcePage.getRotation()?.angle || 0) % 360 + 360) % 360;
    // The normal marketplace label exports used by the tool are unrotated PDF
    // pages. Falling back to raster for unusual page-level rotations keeps the
    // coordinate conversion conservative rather than risking a clipped label.
    if (rotation !== 0) throw new Error(`Unsupported source page rotation: ${rotation}`);

    const cropBox = canvasBoundsToPdfBoundingBox(bounds, sourceCanvas, sourcePage);
    if ((cropBox.right - cropBox.left) < 1 || (cropBox.top - cropBox.bottom) < 1) {
      throw new Error('The detected shipping-label crop is empty.');
    }

    const embeddedPage = await output.embedPage(sourcePage, cropBox);
    const targetBox = {
      x: layout.labelX * MM_TO_PT,
      y: outputPage.getHeight() - ((layout.labelY + layout.labelHeight) * MM_TO_PT),
      width: layout.labelWidth * MM_TO_PT,
      height: layout.labelHeight * MM_TO_PT
    };
    const rotate = Boolean(paperProfile?.autoRotateLabel) && shouldRotateForBox(
      embeddedPage.width,
      embeddedPage.height,
      layout.labelWidth,
      layout.labelHeight
    );

    if (!rotate) {
      const placement = fitRect(
        embeddedPage.width,
        embeddedPage.height,
        targetBox.x,
        targetBox.y,
        targetBox.width,
        targetBox.height
      );
      outputPage.drawPage(embeddedPage, placement);
      return;
    }

    const rotatedPlacement = fitRect(
      embeddedPage.height,
      embeddedPage.width,
      targetBox.x,
      targetBox.y,
      targetBox.width,
      targetBox.height
    );
    outputPage.drawPage(embeddedPage, {
      x: rotatedPlacement.x + rotatedPlacement.width,
      y: rotatedPlacement.y,
      width: rotatedPlacement.height,
      height: rotatedPlacement.width,
      rotate: window.PDFLib.degrees(90)
    });
  }

  function findInkBounds(canvas, region = null, threshold = 245, sampleStep = 3) {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const area = region || { x: 0, y: 0, width: canvas.width, height: canvas.height };
    const startX = clamp(Math.floor(area.x), 0, canvas.width - 1);
    const startY = clamp(Math.floor(area.y), 0, canvas.height - 1);
    const endX = clamp(Math.ceil(area.x + area.width), 1, canvas.width);
    const endY = clamp(Math.ceil(area.y + area.height), 1, canvas.height);
    let minX = endX;
    let minY = endY;
    let maxX = startX;
    let maxY = startY;
    let found = false;

    for (let y = startY; y < endY; y += sampleStep) {
      for (let x = startX; x < endX; x += sampleStep) {
        const offset = (y * canvas.width + x) * 4;
        const brightness = (data[offset] + data[offset + 1] + data[offset + 2]) / 3;
        if (brightness < threshold) {
          found = true;
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    if (!found) return { x: startX, y: startY, width: endX - startX, height: endY - startY };
    const margin = Math.max(10, Math.round(Math.min(canvas.width, canvas.height) * 0.012));
    const x = clamp(minX - margin, startX, endX - 1);
    const y = clamp(minY - margin, startY, endY - 1);
    const right = clamp(maxX + margin, x + 1, endX);
    const bottom = clamp(maxY + margin, y + 1, endY);
    return { x, y, width: right - x, height: bottom - y };
  }

  function textBoxNormalised(box) {
    return normaliseSearchText(box?.text || '');
  }

  function boxesShareLine(left, right) {
    const leftCentre = left.y + (left.height / 2);
    const rightCentre = right.y + (right.height / 2);
    return Math.abs(leftCentre - rightCentre) <= Math.max(6, Math.max(left.height, right.height) * 0.9);
  }

  async function getPdfTextBoxes(pdf, pageIndex, canvas) {
    const page = await pdf.getPage(pageIndex + 1);
    const unitViewport = page.getViewport({ scale: 1 });
    const scale = canvas.width / unitViewport.width;
    const viewport = page.getViewport({ scale });
    const content = await page.getTextContent({ normalizeWhitespace: true });

    return content.items
      .filter((item) => item.str && normaliseWhitespace(item.str))
      .map((item) => {
        const transformed = window.pdfjsLib.Util.transform(viewport.transform, item.transform);
        const height = Math.max(1, Math.hypot(transformed[2], transformed[3]));
        const width = Math.max(1, (item.width || 0) * scale);
        return {
          text: normaliseWhitespace(item.str),
          x: transformed[4],
          y: transformed[5] - height,
          width,
          height,
          right: transformed[4] + width,
          bottom: transformed[5]
        };
      });
  }

  function scaleNormalisedBoxes(boxes, canvas) {
    return (boxes || []).map((box) => ({
      text: box.text,
      x: box.x * canvas.width,
      y: box.y * canvas.height,
      width: box.width * canvas.width,
      height: box.height * canvas.height,
      right: (box.x + box.width) * canvas.width,
      bottom: (box.y + box.height) * canvas.height
    }));
  }

  function parseTesseractTsv(tsv, canvas) {
    if (!tsv) return [];
    const boxes = [];
    const lines = String(tsv).split(/\r?\n/);
    for (const line of lines.slice(1)) {
      if (!line.trim()) continue;
      const columns = line.split('\t');
      if (columns.length < 12 || columns[0] !== '5') continue;
      const text = normaliseWhitespace(columns.slice(11).join('\t'));
      if (!text) continue;
      const left = Number(columns[6]);
      const top = Number(columns[7]);
      const width = Number(columns[8]);
      const height = Number(columns[9]);
      if (![left, top, width, height].every(Number.isFinite) || width <= 0 || height <= 0) continue;
      boxes.push({
        text,
        x: left / canvas.width,
        y: top / canvas.height,
        width: width / canvas.width,
        height: height / canvas.height
      });
    }
    return boxes;
  }

  function groupTextBoxesIntoRows(boxes) {
    const rows = [];
    const sorted = [...boxes].sort((a, b) => (a.y - b.y) || (a.x - b.x));
    for (const box of sorted) {
      const centre = box.y + (box.height / 2);
      let best = null;
      let bestDistance = Infinity;
      for (const row of rows) {
        const distance = Math.abs(row.centre - centre);
        const tolerance = Math.max(7, Math.max(row.height, box.height) * 0.85);
        if (distance <= tolerance && distance < bestDistance) {
          best = row;
          bestDistance = distance;
        }
      }
      if (!best) {
        best = { boxes: [], centre, height: box.height };
        rows.push(best);
      }
      best.boxes.push(box);
      best.height = Math.max(best.height, box.height);
      best.centre = best.boxes.reduce((sum, item) => sum + item.y + (item.height / 2), 0) / best.boxes.length;
    }

    return rows.map((row) => {
      const rowBoxes = row.boxes.sort((a, b) => a.x - b.x);
      const x = Math.min(...rowBoxes.map((box) => box.x));
      const y = Math.min(...rowBoxes.map((box) => box.y));
      const right = Math.max(...rowBoxes.map((box) => box.right));
      const bottom = Math.max(...rowBoxes.map((box) => box.bottom));
      return {
        boxes: rowBoxes,
        text: normaliseSearchText(rowBoxes.map((box) => box.text).join(' ')),
        x,
        y,
        right,
        bottom,
        width: right - x,
        height: bottom - y
      };
    }).sort((a, b) => a.y - b.y);
  }

  function findTextBox(boxes, predicate) {
    const matches = boxes.filter((box) => predicate(textBoxNormalised(box), box));
    if (!matches.length) return null;
    return matches.sort((a, b) => (a.width * a.height) - (b.width * b.height))[0];
  }

  function expandBounds(bounds, canvas, marginX, marginY) {
    const x = clamp(bounds.x - marginX, 0, canvas.width - 1);
    const y = clamp(bounds.y - marginY, 0, canvas.height - 1);
    const right = clamp(bounds.x + bounds.width + marginX, x + 1, canvas.width);
    const bottom = clamp(bounds.y + bounds.height + marginY, y + 1, canvas.height);
    return { x, y, width: right - x, height: bottom - y };
  }

  function parseQuantityValue(text) {
    const raw = normaliseWhitespace(text);
    const cleanedNumber = raw.replace(/[Oo]/g, '0').replace(/[Il|]/g, '1');
    if (/^\d{1,3}$/.test(cleanedNumber)) {
      const value = Number.parseInt(cleanedNumber, 10);
      return Number.isFinite(value) ? value : null;
    }

    // Shopify and a few marketplace packing-slip layouts can express the
    // quantity as "2 of 2" (or occasionally "2/2") rather than a bare number.
    // Keep the word "of" intact while still correcting OCR confusions inside
    // the number tokens themselves.
    const progressMatch = /^([0-9OoIl|]{1,3})\s*(?:of|\/)\s*([0-9OoIl|]{1,3})$/i.exec(raw);
    if (!progressMatch) return null;
    const first = progressMatch[1].replace(/[Oo]/g, '0').replace(/[Il|]/g, '1');
    const value = Number.parseInt(first, 10);
    return Number.isFinite(value) ? value : null;
  }

  function quantityDisplayText(text, value) {
    const raw = normaliseWhitespace(text);
    const progressMatch = /^([0-9OoIl|]{1,3})\s*(?:of|\/)\s*([0-9OoIl|]{1,3})$/i.exec(raw);
    if (progressMatch) {
      const first = progressMatch[1].replace(/[Oo]/g, '0').replace(/[Il|]/g, '1');
      const second = progressMatch[2].replace(/[Oo]/g, '0').replace(/[Il|]/g, '1');
      return `${Number.parseInt(first, 10)} of ${Number.parseInt(second, 10)}`;
    }
    return String(value);
  }

  function isQuantityHeaderText(text) {
    const value = normaliseSearchText(text);
    return value === 'qty' || value.includes('quantity') || value.startsWith('qty ');
  }

  function isProductHeaderText(text) {
    const value = normaliseSearchText(text);
    return value.includes('product') || value.includes('details') || value.includes('description') ||
      value === 'item' || value === 'items' || value.startsWith('item ') || value.startsWith('items ') ||
      value.includes('product name');
  }

  function isPriceHeaderText(text) {
    const value = normaliseSearchText(text);
    return value.includes('price') || value.includes('subtotal') || value.includes('total') || value.includes('amount');
  }

  function isPublicPackingFooterText(text) {
    const value = normaliseSearchText(text);
    return value.includes('thanks for buying') ||
      value.includes('amazon.co.uk/feedback') ||
      value.includes('to contact the seller') ||
      value.includes('further information') ||
      value.includes('appropriate product') ||
      value.includes('thank you for shopping with us') ||
      value.includes('love your purchase') ||
      value.includes('feedback for the seller') ||
      value.includes('questions about your order');
  }

  function isExplicitProductHeaderRow(row) {
    if (!row) return false;
    const value = normaliseSearchText(row.text);
    // This is deliberately much stricter than isProductHeaderText(). The latter
    // is useful when inspecting individual boxes, but a whole sentence in an
    // Amazon footer can contain the word "product" (for example "appropriate
    // product"). Treating that sentence as a table header caused the crop to
    // start in the footer and leave the actual order items off the integrated
    // sheet. Only accept rows that genuinely look like standalone headings.
    return value === 'product' ||
      value === 'product details' ||
      value === 'product detail' ||
      value === 'product description' ||
      value === 'description' ||
      value === 'product name' ||
      value === 'item' ||
      value === 'items';
  }

  function isAmazonItemsCountHeader(row) {
    if (!row) return false;
    const value = normaliseSearchText(row.text);
    // Newer Amazon packing slips can omit the traditional Quantity / Product
    // table heading and instead introduce the fulfilment section with a line
    // such as "1 Items" or "3 Items". This line always sits below the
    // recipient address and above the first product, so it is a privacy-safe
    // crop anchor. Keep the expression deliberately strict so a product title
    // containing the word "items" cannot be mistaken for the heading.
    if (/^\d{1,3} items?$/.test(value)) return true;

    const texts = (row.boxes || []).map((box) => textBoxNormalised(box)).filter(Boolean);
    const hasItemsWord = texts.some((text) => text === 'item' || text === 'items');
    const hasCount = texts.some((text) => /^\d{1,3}$/.test(text));
    return hasItemsWord && hasCount && value.length <= 18;
  }

  function isPackingTableHeader(row, salesChannel = '') {
    const boxTexts = row.boxes.map((box) => textBoxNormalised(box));
    const hasQuantity = boxTexts.some(isQuantityHeaderText) || /(^| )qty( |$)/.test(row.text) || row.text.includes('quantity');
    const hasProduct = boxTexts.some(isProductHeaderText) || isProductHeaderText(row.text);
    const hasPrice = boxTexts.some(isPriceHeaderText) || isPriceHeaderText(row.text);
    const productOnlyChannel = salesChannel === 'amazon' || salesChannel === 'temu' || salesChannel === 'tiktok' || salesChannel === 'shopify';

    // Amazon compact slips, Temu, TikTok and Shopify packing pages can contain only item/product
    // details plus a quantity column. Shopify's native packing slip typically
    // labels these columns "ITEMS" and "QUANTITY" and does not print prices,
    // so requiring a price/total heading would incorrectly reject the page.
    return hasQuantity && hasProduct && (hasPrice || productOnlyChannel);
  }

  function findPackingTableHeader(rows, salesChannel = '') {
    return rows.find((row) => isPackingTableHeader(row, salesChannel)) || null;
  }

  function findProductOnlyPackingHeader(rows, salesChannel = '') {
    const strictHeader = findPackingTableHeader(rows, salesChannel);
    if (strictHeader) return strictHeader;

    if (salesChannel !== 'amazon' && salesChannel !== 'temu' && salesChannel !== 'tiktok' && salesChannel !== 'shopify') return null;

    // Some marketplace PDFs split the visible table heading across separate
    // PDF text rows. For these product-only layouts, the first Product Details
    // or Items heading is a safe crop anchor once it occurs below the address
    // block. Prefer a row with a nearby quantity heading where possible.
    // Amazon's newer order-summary packing slip uses an item-count line
    // (for example "1 Items") rather than a Quantity / Product header.
    // It is below the shipping address, so starting the privacy-safe crop here
    // retains every item while excluding the recipient details above it.
    if (salesChannel === 'amazon') {
      const itemCountRow = rows.find((row) => isAmazonItemsCountHeader(row));
      if (itemCountRow) return itemCountRow;
    }

    const productRows = rows.filter((row) => (
      isProductHeaderText(row.text) && !isPublicPackingFooterText(row.text)
    ));
    if (!productRows.length) return null;

    for (const productRow of productRows) {
      const nearbyQuantity = rows.find((row) => {
        if (row === productRow) return false;
        const verticalGap = Math.abs(row.y - productRow.y);
        const tolerance = Math.max(productRow.height, row.height) * 1.8 + 10;
        return verticalGap <= tolerance && (
          row.boxes.some((box) => isQuantityHeaderText(textBoxNormalised(box))) ||
          /(^| )(qty|quantity)( |$)/.test(row.text)
        );
      });
      if (nearbyQuantity) return productRow;
    }

    // Never fall back to an arbitrary sentence merely because it contains the
    // word "product". On image-only Amazon slips OCR can miss the real table
    // heading but still read the standard footer sentence. Returning that footer
    // here produced an apparently blank packing summary containing only the end
    // of "appropriate product...". If there is no explicit heading, return
    // null so the full-page OCR / ruled-table visual fallback can take over.
    return productRows.find((row) => isExplicitProductHeaderRow(row)) || null;
  }

  function findSafePackingTableBoundsByLines(canvas) {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const data = context.getImageData(0, 0, canvas.width, canvas.height).data;
    const startX = Math.round(canvas.width * 0.02);
    const endX = Math.round(canvas.width * 0.98);
    const startY = Math.round(canvas.height * 0.20);
    const endY = Math.round(canvas.height * 0.82);
    const stepX = 2;
    const requiredFraction = 0.46;
    const candidateRows = [];

    for (let y = startY; y < endY; y += 1) {
      let dark = 0;
      let samples = 0;
      for (let x = startX; x < endX; x += stepX) {
        const offset = (y * canvas.width + x) * 4;
        const brightness = (data[offset] + data[offset + 1] + data[offset + 2]) / 3;
        if (brightness < 225) dark += 1;
        samples += 1;
      }
      if (samples && (dark / samples) >= requiredFraction) candidateRows.push(y);
    }

    if (!candidateRows.length) return null;
    const bands = [];
    for (const y of candidateRows) {
      const last = bands[bands.length - 1];
      if (!last || y > last.end + 2) bands.push({ start: y, end: y });
      else last.end = y;
    }

    // Locate the first close group of long horizontal rules. On marketplace
    // slips this is normally the recipient-box bottom immediately followed by
    // the product-table top/header rules, or the product-table rules themselves.
    // Starting at the first rule in that close group is privacy-safe because
    // recipient text sits above the recipient-box bottom.
    const maximumGap = canvas.height * 0.045;
    let firstBandIndex = -1;
    for (let index = 0; index < bands.length - 1; index += 1) {
      const current = bands[index];
      const next = bands[index + 1];
      const gap = next.start - current.end;
      if (current.start >= canvas.height * 0.30 && gap >= 0 && gap <= maximumGap) {
        firstBandIndex = index;
        break;
      }
    }
    if (firstBandIndex < 0) return null;
    const firstBand = bands[firstBandIndex];
    const safeTop = clamp(firstBand.start, startY, canvas.height - 1);

    let clusterEndIndex = firstBandIndex;
    while (clusterEndIndex + 1 < bands.length) {
      const gap = bands[clusterEndIndex + 1].start - bands[clusterEndIndex].end;
      if (gap < 0 || gap > maximumGap) break;
      clusterEndIndex += 1;
    }
    const tableBottomBand = bands[clusterEndIndex + 1] || null;
    const safeBottom = tableBottomBand
      ? Math.min(canvas.height * 0.86, tableBottomBand.end + (canvas.height * 0.038))
      : Math.min(canvas.height * 0.86, safeTop + (canvas.height * 0.36));
    const region = {
      x: canvas.width * 0.018,
      y: safeTop,
      width: canvas.width * 0.964,
      height: Math.max(1, safeBottom - safeTop)
    };
    const ink = findInkBounds(canvas, region, 248, 2);
    const expanded = expandBounds(ink, canvas, canvas.width * 0.006, canvas.height * 0.002);
    const bottom = expanded.y + expanded.height;
    expanded.y = Math.max(safeTop, expanded.y);
    expanded.height = Math.max(1, bottom - expanded.y);
    return expanded;
  }

  function locateQuantityHeaderBox(row) {
    const direct = row.boxes.find((box) => isQuantityHeaderText(textBoxNormalised(box)));
    if (direct) return direct;

    for (const box of row.boxes) {
      const text = normaliseSearchText(box.text);
      const match = /(^| )(quantity|qty)( |$)/.exec(text);
      if (!match) continue;
      const token = match[2];
      const tokenStart = match.index + (match[1] ? match[1].length : 0);
      const ratioStart = tokenStart / Math.max(1, text.length);
      const ratioWidth = token.length / Math.max(1, text.length);
      const x = box.x + (box.width * ratioStart);
      const width = Math.max(box.width * ratioWidth, box.height * 1.5);
      return {
        ...box,
        text: token,
        x,
        width,
        right: x + width
      };
    }
    return null;
  }

  async function emphasiseMultiItemQuantities(pdf, pageIndex, canvas, ocrBoxes = [], salesChannel = '') {
    let boxes = await getPdfTextBoxes(pdf, pageIndex, canvas);
    let rows = groupTextBoxesIntoRows(boxes);
    let headerRow = findPackingTableHeader(rows, salesChannel);

    if (!headerRow && ocrBoxes.length) {
      boxes = scaleNormalisedBoxes(ocrBoxes, canvas);
      rows = groupTextBoxesIntoRows(boxes);
      headerRow = findPackingTableHeader(rows, salesChannel);
    }
    if (!headerRow) return 0;

    const quantityHeader = locateQuantityHeaderBox(headerRow);
    let columnLeft = 0;
    let columnRight = 0;

    if (quantityHeader) {
      const orderedHeaderBoxes = [...headerRow.boxes].sort((a, b) => a.x - b.x);
      const previousHeader = [...orderedHeaderBoxes].reverse().find((box) => box.right <= quantityHeader.x - 1) || null;
      const nextHeader = orderedHeaderBoxes.find((box) => box.x >= quantityHeader.right + 1) || null;
      columnLeft = Math.max(
        0,
        previousHeader
          ? (previousHeader.right + quantityHeader.x) / 2
          : quantityHeader.x - (canvas.width * 0.045)
      );
      columnRight = Math.min(
        canvas.width,
        nextHeader
          ? (quantityHeader.right + nextHeader.x) / 2
          : quantityHeader.right + (canvas.width * 0.045)
      );
    } else if (salesChannel === 'amazon' && isAmazonItemsCountHeader(headerRow)) {
      // Amazon's newer compact order-summary slip can use "2 Items" as the
      // section heading and omit a literal Quantity column heading. The quantity
      // itself is still the left-most value beneath that heading. Restrict the
      // fallback to the left edge of this known Amazon layout so prices/order
      // numbers elsewhere on the page can never be mistaken for quantities.
      columnLeft = 0;
      columnRight = canvas.width * 0.20;
    } else {
      return 0;
    }

    if (columnRight <= columnLeft) return 0;

    const footerRow = rows.find((row) => row.y > headerRow.y && (
      (row.text.includes('grand') && row.text.includes('total')) ||
      row.text.includes('item subtotal') ||
      row.text.includes('item total') ||
      row.text.includes('qty total') ||
      row.text.includes('order id') ||
      row.text.includes('buyer id')
    ));
    const tableBottom = footerRow?.bottom ?? Math.min(canvas.height * 0.78, headerRow.y + (canvas.height * 0.38));
    const candidates = boxes.map((box) => {
      const centreX = box.x + (box.width / 2);
      const value = parseQuantityValue(box.text);
      return { box, centreX, value };
    }).filter((candidate) => (
      candidate.value != null && candidate.value > 1 &&
      candidate.box.y > headerRow.bottom + 2 && candidate.box.bottom < tableBottom &&
      candidate.centreX > columnLeft && candidate.centreX < columnRight
    ));
    if (!candidates.length) return 0;

    const context = canvas.getContext('2d', { alpha: false });
    context.save();
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillStyle = '#000000';

    for (const candidate of candidates) {
      const { box, value } = candidate;
      const displayText = quantityDisplayText(box.text, value);
      const cellWidth = Math.max(1, columnRight - columnLeft);
      const patchWidth = Math.min(cellWidth * 0.86, Math.max(box.width * 3.4, cellWidth * 0.60));
      const patchHeight = Math.max(box.height * 2.20, canvas.height * 0.021);
      const centreX = clamp(box.x + (box.width / 2), columnLeft + (patchWidth / 2), columnRight - (patchWidth / 2));
      const centreY = box.y + (box.height / 2);
      const patchX = centreX - (patchWidth / 2);
      const patchY = centreY - (patchHeight / 2);

      context.fillStyle = '#ffffff';
      context.fillRect(patchX, patchY, patchWidth, patchHeight);

      // Make multi-quantity cells clearly noticeable without making the number
      // look out of place compared with the rest of the packing slip.
      let fontSize = Math.max(box.height * 1.65, canvas.height * 0.0165);
      const maximumHeight = patchHeight * 0.72;
      fontSize = Math.min(fontSize, maximumHeight);
      context.font = `800 ${fontSize}px Arial, Helvetica, sans-serif`;
      const maximumTextWidth = patchWidth * 0.84;
      const measured = context.measureText(displayText).width;
      if (measured > maximumTextWidth && measured > 0) {
        fontSize *= maximumTextWidth / measured;
        context.font = `800 ${fontSize}px Arial, Helvetica, sans-serif`;
      }

      context.fillStyle = '#000000';
      context.fillText(displayText, centreX, centreY + (fontSize * 0.03));
    }

    context.restore();
    return candidates.length;
  }

  async function findPackingProductBounds(pdf, pageIndex, canvas, ocrBoxes = [], salesChannel = '') {
    let boxes = await getPdfTextBoxes(pdf, pageIndex, canvas);
    let rows = groupTextBoxesIntoRows(boxes);
    let headerRow = findProductOnlyPackingHeader(rows, salesChannel);

    if (!headerRow && ocrBoxes.length) {
      boxes = scaleNormalisedBoxes(ocrBoxes, canvas);
      rows = groupTextBoxesIntoRows(boxes);
      headerRow = findProductOnlyPackingHeader(rows, salesChannel);
    }

    // Defensive guard: a marketplace footer must never be used as the crop
    // anchor, even if OCR has split or distorted the real table heading.
    if (headerRow && isPublicPackingFooterText(headerRow.text)) headerRow = null;

    if (headerRow) {
      const rowsAfterHeader = rows.filter((row) => row.y > headerRow.y);

      // Temu and TikTok packing pages do not contain a recipient address in
      // the packing document used by this workflow. Crop from the recognised
      // Product Details / Qty header through the final visible product row.
      // This avoids applying Amazon's address-table rules to a different layout
      // and also preserves long multi-item Temu/TikTok orders.
      if (salesChannel === 'temu' || salesChannel === 'tiktok') {
        const safeHeaderTop = Math.max(0, headerRow.y - (canvas.height * 0.004));
        const region = {
          x: canvas.width * 0.012,
          y: safeHeaderTop,
          width: canvas.width * 0.976,
          height: Math.max(1, canvas.height - safeHeaderTop - (canvas.height * 0.012))
        };
        const ink = findInkBounds(canvas, region, 248, 2);
        const expanded = expandBounds(ink, canvas, canvas.width * 0.008, canvas.height * 0.004);
        const expandedBottom = expanded.y + expanded.height;
        expanded.y = Math.max(safeHeaderTop, expanded.y);
        expanded.height = Math.max(1, expandedBottom - expanded.y);
        return expanded;
      }
      // Shopify's standard packing slip places SHIP TO / BILL TO above the
      // ITEMS / QUANTITY header. Starting the crop at that header therefore
      // excludes the customer's address. The "Thank you for shopping with us!"
      // footer below the item rows is used as the lower boundary when present.
      const privateFooter = rowsAfterHeader.find((row) => (
        row.text.includes('buyer id') ||
        row.text.includes('buyer name') ||
        row.text.includes('nickname') ||
        row.text.includes('delivery address') ||
        row.text.includes('shipping address')
      ));
      const orderFooter = rowsAfterHeader.find((row) => (
        row.text.includes('order id') ||
        row.text.includes('order number') ||
        row.text.includes('customer reference')
      ));
      const grandTotalRow = rowsAfterHeader.find((row) => row.text.includes('grand') && row.text.includes('total'));
      const itemTotalRows = rowsAfterHeader.filter((row) => (
        row.text.includes('item total') ||
        row.text.includes('item subtotal') ||
        row.text.includes('order total')
      ));

      // Public marketplace footer text is safe, but it is not part of the
      // product summary. More importantly, using it as a lower boundary lets
      // long Amazon tables grow naturally instead of being clipped by the old
      // fixed-height fallback when there are three or more different SKUs.
      const publicFooter = rowsAfterHeader.find((row) => (
        isPublicPackingFooterText(row.text) || /^page \d+ of \d+$/.test(row.text)
      ));

      let bottom = 0;
      if (grandTotalRow) bottom = grandTotalRow.bottom;
      if (!bottom && itemTotalRows.length) bottom = Math.max(...itemTotalRows.map((row) => row.bottom));
      if (!bottom && publicFooter) bottom = Math.max(headerRow.bottom + (canvas.height * 0.07), publicFooter.y - (canvas.height * 0.008));
      if (!bottom && orderFooter) bottom = Math.max(headerRow.bottom + (canvas.height * 0.07), orderFooter.y - (canvas.height * 0.008));
      if (!bottom && privateFooter) bottom = Math.max(headerRow.bottom + (canvas.height * 0.07), privateFooter.y - (canvas.height * 0.008));
      // Once the product-table header has been safely located, everything below
      // it can be inspected without reintroducing the dispatch-address block.
      // Keep almost the whole remaining page as a last resort rather than
      // silently dropping later product rows.
      if (!bottom) bottom = canvas.height * 0.94;

      if (publicFooter) bottom = Math.min(bottom, publicFooter.y - (canvas.height * 0.006));
      if (privateFooter) bottom = Math.min(bottom, privateFooter.y - (canvas.height * 0.006));
      const safeHeaderTop = Math.max(0, headerRow.y - (canvas.height * 0.003));
      const region = {
        x: canvas.width * 0.025,
        y: safeHeaderTop,
        width: canvas.width * 0.95,
        height: Math.max(canvas.height * 0.07, bottom - headerRow.y + (canvas.height * 0.018))
      };
      const ink = findInkBounds(canvas, region, 248, 2);
      const expanded = expandBounds(ink, canvas, canvas.width * 0.008, canvas.height * 0.002);
      const expandedBottom = expanded.y + expanded.height;
      expanded.y = Math.max(safeHeaderTop, expanded.y);
      expanded.height = Math.max(1, expandedBottom - expanded.y);
      return expanded;
    }

    // Strict privacy fallback: never begin in the recipient/address block.
    // A long horizontal rule in the lower half marks either the bottom of that
    // block or the top of the product table. If it cannot be located, stop
    // rather than risk copying personal data into the disposable section.
    const safeVisualBounds = findSafePackingTableBoundsByLines(canvas);
    if (safeVisualBounds) return safeVisualBounds;
    return null;
  }

  async function createProductOnlyPackingCanvas(pdf, pageIndex, canvas, ocrBoxes = [], salesChannel = '') {
    const bounds = await findPackingProductBounds(pdf, pageIndex, canvas, ocrBoxes, salesChannel);
    if (!bounds) {
      throw new Error(
        `Packing-slip page ${pageIndex + 1} was not generated because the product table could not be located safely for ${salesChannel || 'this sales channel'}. ` +
        'Try a clearer PDF or send a sample layout for calibration.'
      );
    }
    return cropCanvas(canvas, bounds.x, bounds.y, bounds.width, bounds.height);
  }

  function packingSlipHasPostcode(packingRecord) {
    return Array.isArray(packingRecord?.postcodes) && packingRecord.postcodes.length > 0;
  }

  function selectDisplayPostcode(shippingRecord, packingRecord) {
    const shippingPostcodes = Array.isArray(shippingRecord?.postcodes) ? shippingRecord.postcodes : [];
    const packingPostcodes = Array.isArray(packingRecord?.postcodes) ? packingRecord.postcodes : [];
    const shared = packingPostcodes.find((postcode) => shippingPostcodes.includes(postcode));
    return shared || packingPostcodes[0] || shippingPostcodes[0] || '';
  }

  function addPostcodeHeaderToCanvas(sourceCanvas, postcode) {
    const headerHeight = Math.max(64, Math.round(sourceCanvas.width * 0.055));
    const gap = Math.max(8, Math.round(sourceCanvas.width * 0.008));
    const output = document.createElement('canvas');
    output.width = sourceCanvas.width;
    output.height = sourceCanvas.height + headerHeight + gap;

    const context = output.getContext('2d', { alpha: false });
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, output.width, output.height);

    const inset = Math.max(4, Math.round(output.width * 0.004));
    context.strokeStyle = '#000000';
    context.lineWidth = Math.max(2, Math.round(output.width * 0.002));
    context.strokeRect(
      inset,
      inset,
      output.width - (inset * 2),
      headerHeight - (inset * 2)
    );

    const label = postcode ? `CHECK POSTCODE: ${postcode}` : 'CHECK POSTCODE: NOT DETECTED';
    let fontSize = Math.min(headerHeight * 0.47, output.width * 0.038);
    context.font = `900 ${fontSize}px Arial, Helvetica, sans-serif`;
    const maximumWidth = output.width * 0.91;
    const measured = context.measureText(label).width;
    if (measured > maximumWidth && measured > 0) {
      fontSize *= maximumWidth / measured;
      context.font = `900 ${fontSize}px Arial, Helvetica, sans-serif`;
    }
    context.fillStyle = '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(label, output.width / 2, headerHeight / 2);

    context.drawImage(sourceCanvas, 0, headerHeight + gap);
    return output;
  }

  async function redactPackingCanvas(pdf, pageIndex, canvas, ocrBoxes = []) {
    const context = canvas.getContext('2d', { alpha: false });
    let boxes = await getPdfTextBoxes(pdf, pageIndex, canvas);
    if (!boxes.length && ocrBoxes.length) boxes = scaleNormalisedBoxes(ocrBoxes, canvas);
    const rows = groupTextBoxesIntoRows(boxes);
    const width = canvas.width;
    const height = canvas.height;
    const masks = [];

    const dispatchRow = rows.find((row) => row.text.includes('dispatch to'));
    const orderIdRow = rows.find((row) => row.text.includes('order id'));
    if (dispatchRow && orderIdRow && orderIdRow.y > dispatchRow.y) {
      masks.push({
        x: Math.max(0, dispatchRow.x - (width * 0.008)),
        y: Math.max(0, dispatchRow.y - (height * 0.005)),
        width: Math.min(width * 0.62, width - dispatchRow.x),
        height: Math.max(1, orderIdRow.y - dispatchRow.y - (height * 0.006))
      });
    }

    const deliveryRow = rows.find((row) => row.text.includes('delivery address'));
    const tableHeaderRow = rows.find((row) => (
      row.text.includes('quantity') &&
      (row.text.includes('product') || row.text.includes('details'))
    ));
    if (deliveryRow && tableHeaderRow && tableHeaderRow.y > deliveryRow.y) {
      const orderWord = deliveryRow.boxes.find((box) => box.x > deliveryRow.x && textBoxNormalised(box) === 'order');
      const right = orderWord ? orderWord.x - (width * 0.012) : width * 0.31;
      masks.push({
        x: Math.max(0, deliveryRow.x - (width * 0.004)),
        y: Math.min(height, deliveryRow.bottom + (height * 0.002)),
        width: Math.max(1, right - deliveryRow.x),
        height: Math.max(1, tableHeaderRow.y - deliveryRow.bottom - (height * 0.006))
      });
    }

    const buyerRow = rows.find((row) => row.text.includes('buyer name'));
    if (buyerRow) {
      const nameMarker = buyerRow.boxes.find((box) => textBoxNormalised(box).startsWith('name'));
      const valueStart = nameMarker ? nameMarker.right : width * 0.47;
      const values = buyerRow.boxes.filter((box) => box.x > valueStart + 3);
      if (values.length) {
        const left = Math.min(...values.map((box) => box.x));
        const top = Math.min(...values.map((box) => box.y));
        const right = Math.max(...values.map((box) => box.right));
        const bottom = Math.max(...values.map((box) => box.bottom));
        masks.push({
          x: left - (width * 0.004),
          y: top - (height * 0.003),
          width: right - left + (width * 0.008),
          height: bottom - top + (height * 0.006)
        });
      }
    }

    // If the image-only page could not be interpreted, use conservative masks
    // aligned to Amazon's standard A4 packing-slip layout. These stop above the
    // product table and therefore cannot obscure product names or prices.
    if (!masks.length) {
      masks.push(
        { x: width * 0.055, y: height * 0.055, width: width * 0.55, height: height * 0.14 },
        { x: width * 0.055, y: height * 0.275, width: width * 0.25, height: height * 0.09 },
        { x: width * 0.49, y: height * 0.295, width: width * 0.22, height: height * 0.025 }
      );
    }

    context.save();
    context.fillStyle = '#ffffff';
    for (const mask of masks) context.fillRect(mask.x, mask.y, mask.width, mask.height);
    context.restore();
  }

  function canvasToBytes(canvas, type = 'image/png', quality = 0.92) {
    return new Promise((resolve, reject) => {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          reject(new Error('Could not convert the page image.'));
          return;
        }
        resolve(new Uint8Array(await blob.arrayBuffer()));
      }, type, quality);
    });
  }

  function fitRect(sourceWidth, sourceHeight, boxX, boxY, boxWidth, boxHeight) {
    const scale = Math.min(boxWidth / sourceWidth, boxHeight / sourceHeight);
    const width = sourceWidth * scale;
    const height = sourceHeight * scale;
    return {
      x: boxX + (boxWidth - width) / 2,
      y: boxY + (boxHeight - height) / 2,
      width,
      height
    };
  }

  function fittedWidthFraction(sourceWidth, sourceHeight, boxWidth, boxHeight) {
    if (![sourceWidth, sourceHeight, boxWidth, boxHeight].every((value) => Number.isFinite(value) && value > 0)) return 1;
    const placement = fitRect(sourceWidth, sourceHeight, 0, 0, boxWidth, boxHeight);
    return placement.width / boxWidth;
  }

  function rowDarkFraction(canvas, y, startFraction = 0.015, endFraction = 0.985, step = 4) {
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const row = clamp(Math.round(y), 0, canvas.height - 1);
    const xStart = Math.round(canvas.width * startFraction);
    const xEnd = Math.round(canvas.width * endFraction);
    const pixels = context.getImageData(0, row, canvas.width, 1).data;
    let dark = 0;
    let samples = 0;
    for (let x = xStart; x < xEnd; x += step) {
      const offset = x * 4;
      const brightness = (pixels[offset] + pixels[offset + 1] + pixels[offset + 2]) / 3;
      if (brightness < 210) dark += 1;
      samples += 1;
    }
    return samples ? dark / samples : 0;
  }

  function findSafePackingSplitY(canvas, startY, desiredHeight) {
    const desiredY = Math.min(canvas.height - 1, Math.round(startY + desiredHeight));
    const minimumY = Math.min(canvas.height - 1, Math.round(startY + Math.max(80, desiredHeight * 0.55)));
    const maximumY = Math.min(canvas.height - 1, Math.round(startY + (desiredHeight * 1.08)));
    if (maximumY <= minimumY) return desiredY;

    // Prefer a long horizontal table rule so an individual SKU row is not cut
    // in half. Amazon/Temu/TikTok packing tables normally provide these rules.
    let bestRule = null;
    let ruleDistance = Infinity;
    for (let y = minimumY; y <= maximumY; y += 2) {
      const fraction = rowDarkFraction(canvas, y, 0.02, 0.98, 4);
      if (fraction < 0.42) continue;
      const distance = Math.abs(y - desiredY);
      if (distance < ruleDistance) {
        bestRule = y;
        ruleDistance = distance;
      }
    }
    if (bestRule != null) return bestRule + 2;

    // If there is no ruled table, split in the nearest mostly-white gap.
    let bestGap = null;
    let gapDistance = Infinity;
    for (let y = minimumY; y <= maximumY; y += 2) {
      const fraction = rowDarkFraction(canvas, y, 0.03, 0.97, 5);
      if (fraction > 0.012) continue;
      const distance = Math.abs(y - desiredY);
      if (distance < gapDistance) {
        bestGap = y;
        gapDistance = distance;
      }
    }
    return bestGap != null ? bestGap : desiredY;
  }

  function splitPackingCanvasForOutput(sourceCanvas, layout) {
    const NOTE_HEIGHT_MM = 9;
    const FIRST_PAGE_MIN_WIDTH_FRACTION = 0.72;
    const CONTINUATION_MIN_WIDTH_FRACTION = 0.86;
    const CONTINUATION_WIDTH_MM = 190;
    const CONTINUATION_HEIGHT_MM = 247;

    const fullWidthFraction = fittedWidthFraction(
      sourceCanvas.width,
      sourceCanvas.height,
      layout.packingWidth,
      layout.packingHeight
    );
    if (fullWidthFraction >= FIRST_PAGE_MIN_WIDTH_FRACTION) return [sourceCanvas];

    const firstUsableHeightMm = Math.max(30, layout.packingHeight - NOTE_HEIGHT_MM);
    const firstMaxHeightPx = sourceCanvas.width * (firstUsableHeightMm / layout.packingWidth) / FIRST_PAGE_MIN_WIDTH_FRACTION;
    const continuationMaxHeightPx = sourceCanvas.width * (CONTINUATION_HEIGHT_MM / CONTINUATION_WIDTH_MM) / CONTINUATION_MIN_WIDTH_FRACTION;
    const chunks = [];
    let startY = 0;
    let targetHeight = firstMaxHeightPx;
    let guard = 0;

    while (startY < sourceCanvas.height - 1 && guard < 20) {
      guard += 1;
      const remaining = sourceCanvas.height - startY;
      if (remaining <= targetHeight * 1.02) {
        chunks.push(cropCanvas(sourceCanvas, 0, startY, sourceCanvas.width, remaining));
        break;
      }
      let splitY = findSafePackingSplitY(sourceCanvas, startY, targetHeight);
      if (splitY <= startY + 30 || splitY >= sourceCanvas.height) {
        splitY = Math.min(sourceCanvas.height, Math.round(startY + targetHeight));
      }
      chunks.push(cropCanvas(sourceCanvas, 0, startY, sourceCanvas.width, splitY - startY));
      startY = splitY;
      targetHeight = continuationMaxHeightPx;
    }

    return chunks.length ? chunks : [sourceCanvas];
  }

  function splitPackingCanvasForThermalOutput(sourceCanvas, targetWidthMm, targetHeightMm) {
    // Thermal packing slips are intentionally rendered landscape so the original
    // marketplace product table can use the full 6-inch edge of the label stock.
    // Never shrink a long table further just to keep it on one page: calculate the
    // maximum source height that fits when the chunk fills the available width,
    // then split at a table rule / whitespace gap. This keeps the text as large as
    // the 6 x 4 media allows and sends additional items to continuation pages.
    const safeWidthMm = Math.max(20, Number(targetWidthMm) || 147.4);
    const safeHeightMm = Math.max(20, Number(targetHeightMm) || 96.6);
    const maxHeightPx = sourceCanvas.width * (safeHeightMm / safeWidthMm);
    if (sourceCanvas.height <= maxHeightPx * 1.01) return [sourceCanvas];

    const chunks = [];
    let startY = 0;
    let guard = 0;

    while (startY < sourceCanvas.height - 1 && guard < 40) {
      guard += 1;
      const remaining = sourceCanvas.height - startY;
      if (remaining <= maxHeightPx * 1.01) {
        chunks.push(cropCanvas(sourceCanvas, 0, startY, sourceCanvas.width, remaining));
        break;
      }

      let splitY = findSafePackingSplitY(sourceCanvas, startY, maxHeightPx);
      if (splitY <= startY + 30 || splitY >= sourceCanvas.height) {
        splitY = Math.min(sourceCanvas.height, Math.round(startY + maxHeightPx));
      }
      chunks.push(cropCanvas(sourceCanvas, 0, startY, sourceCanvas.width, splitY - startY));
      startY = splitY;
    }

    return chunks.length ? chunks : [sourceCanvas];
  }

  function drawContinuationNote(page, box, text = 'PLEASE SEE NEXT PAGE FOR MORE PRODUCTS ORDERED') {
    const noteHeight = 8 * MM_TO_PT;
    const size = 9;
    page.drawText(text, {
      x: box.x + (4 * MM_TO_PT),
      y: box.y + ((noteHeight - size) / 2) + 2,
      size,
      maxWidth: Math.max(20, box.width - (8 * MM_TO_PT))
    });
  }


  function usageMonthKey(date = new Date()) {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return usageMonthKey(new Date());
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}`;
  }

  function normaliseUsageData(value) {
    const source = value && typeof value === 'object' ? value : {};
    const months = {};
    if (source.months && typeof source.months === 'object') {
      Object.entries(source.months).forEach(([key, month]) => {
        if (!/^\d{4}-\d{2}$/.test(key)) return;
        months[key] = {
          orders: Math.max(0, Math.floor(Number(month?.orders) || 0)),
          batches: Math.max(0, Math.floor(Number(month?.batches) || 0))
        };
      });
    }
    return {
      version: 1,
      totalOrders: Math.max(0, Math.floor(Number(source.totalOrders) || 0)),
      totalBatches: Math.max(0, Math.floor(Number(source.totalBatches) || 0)),
      months
    };
  }

  function recordUsageBatch(value, orderCount, date = new Date()) {
    const usage = normaliseUsageData(value);
    const orders = Math.max(0, Math.floor(Number(orderCount) || 0));
    if (!orders) return usage;
    const month = usageMonthKey(date);
    if (!usage.months[month]) usage.months[month] = { orders: 0, batches: 0 };
    usage.months[month].orders += orders;
    usage.months[month].batches += 1;
    usage.totalOrders += orders;
    usage.totalBatches += 1;

    // Keep the local record compact while retaining enough history for future dashboards.
    const monthKeys = Object.keys(usage.months).sort().reverse();
    monthKeys.slice(24).forEach((key) => delete usage.months[key]);
    return usage;
  }

  class IntegratedLabelTool {
    constructor(root) {
      this.root = root;
      this.state = {
        shippingFile: null,
        packingFile: null,
        shippingBytes: null,
        packingBytes: null,
        shippingPdf: null,
        packingPdf: null,
        shippingRecords: [],
        packingRecords: [],
        matches: [],
        packingSafety: {},
        preflightFilter: 'all',
        preflightChecking: false,
        detectedCourierId: '',
        detectedSalesChannelId: '',
        detectionComplete: false,
        shippingDetectionStatus: 'idle',
        packingDetectionStatus: 'idle',
        shippingDetectionToken: 0,
        packingDetectionToken: 0,
        ocrWorker: null,
        downloadUrl: null,
        isBusy: false,
        usage: normaliseUsageData(null)
      };
      this.elements = {};
      this.detectionQueue = Promise.resolve();
      this.bindElements();
      this.bindEvents();
      this.updateFileSummaries();
      this.loadUsage();
      this.renderUsageDashboard();
    }

    bindElements() {
      const q = (selector) => this.root.querySelector(selector);
      this.elements = {
        shippingInput: q('[data-role="shipping-input"]'),
        packingInput: q('[data-role="packing-input"]'),
        shippingSummary: q('[data-role="shipping-summary"]'),
        packingSummary: q('[data-role="packing-summary"]'),
        analyseButton: q('[data-role="analyse"]'),
        resetButton: q('[data-role="reset"]'),
        status: q('[data-role="status"]'),
        statusText: q('[data-role="status-text"]'),
        progress: q('[data-role="progress"]'),
        review: q('[data-role="review"]'),
        matchBody: q('[data-role="match-body"]'),
        batchSummary: q('[data-role="batch-summary"]'),
        preflightPanel: q('[data-role="preflight-panel"]'),
        preflightBadge: q('[data-role="preflight-badge"]'),
        preflightMetrics: q('[data-role="preflight-metrics"]'),
        preflightIssues: q('[data-role="preflight-issues"]'),
        preflightReadiness: q('[data-role="preflight-readiness"]'),
        preflightSubtext: q('[data-role="preflight-subtext"]'),
        preflightDetails: q('[data-role="preflight-details"]'),
        preflightFilterButtons: [...this.root.querySelectorAll('[data-role="preflight-filter"]')],
        removeUnresolvedButton: q('[data-role="remove-unresolved"]'),
        restoreAllButton: q('[data-role="restore-all"]'),
        generateButton: q('[data-role="generate"]'),
        generationProgress: q('[data-role="generation-progress"]'),
        generationProgressText: q('[data-role="generation-progress-text"]'),
        generationProgressPercent: q('[data-role="generation-progress-percent"]'),
        generationProgressBar: q('[data-role="generation-progress-bar"]'),
        preview: q('[data-role="preview"]'),
        previewFrame: q('[data-role="preview-frame"]'),
        download: q('[data-role="download"]'),
        fallback: q('[data-role="fallback"]'),
        redact: q('[data-role="redact"]'),
        compact: q('[data-role="compact"]'),
        autoCrop: q('[data-role="auto-crop"]'),
        labelX: q('[data-role="label-x"]'),
        labelY: q('[data-role="label-y"]'),
        labelWidth: q('[data-role="label-width"]'),
        labelHeight: q('[data-role="label-height"]'),
        packingX: q('[data-role="packing-x"]'),
        packingY: q('[data-role="packing-y"]'),
        packingWidth: q('[data-role="packing-width"]'),
        packingHeight: q('[data-role="packing-height"]'),
        courierInputs: [...this.root.querySelectorAll('[data-role="courier-choice"]')],
        salesChannelInputs: [...this.root.querySelectorAll('[data-role="sales-channel-choice"]')],
        paperInputs: [...this.root.querySelectorAll('[data-role="paper-choice"]')],
        paperSummary: q('[data-role="paper-summary"]'),
        shippingTitle: q('[data-role="shipping-title"]'),
        shippingHelp: q('[data-role="shipping-help"]'),
        packingTitle: q('[data-role="packing-title"]'),
        packingHelp: q('[data-role="packing-help"]'),
        modeSummary: q('[data-role="mode-summary"]'),
        fullSlipWarning: q('[data-role="full-slip-warning"]'),
        autoDetect: q('[data-role="auto-detect"]'),
        detectionSummary: q('[data-role="detection-summary"]'),
        detectedCourier: q('[data-role="detected-courier"]'),
        detectedChannel: q('[data-role="detected-channel"]'),
        manualProfileDetails: q('[data-role="manual-profile-details"]'),
        matchPreviewModal: q('[data-role="match-preview-modal"]'),
        matchPreviewTitle: q('[data-role="match-preview-title"]'),
        matchPreviewReason: q('[data-role="match-preview-reason"]'),
        matchPreviewShippingTitle: q('[data-role="match-preview-shipping-title"]'),
        matchPreviewPackingTitle: q('[data-role="match-preview-packing-title"]'),
        matchPreviewShipping: q('[data-role="match-preview-shipping"]'),
        matchPreviewPacking: q('[data-role="match-preview-packing"]'),
        usageDashboard: q('[data-role="usage-dashboard"]'),
        usageMonthOrders: q('[data-role="usage-month-orders"]'),
        usageMonthBatches: q('[data-role="usage-month-batches"]'),
        usageTotalOrders: q('[data-role="usage-total-orders"]')
      };
    }

    bindEvents() {
      this.elements.shippingInput.addEventListener('change', () => this.handleFile('shipping'));
      this.elements.packingInput.addEventListener('change', () => this.handleFile('packing'));
      this.elements.courierInputs.forEach((input) => input.addEventListener('change', () => this.handleManualProfileChange()));
      this.elements.salesChannelInputs.forEach((input) => input.addEventListener('change', () => this.handleManualProfileChange()));
      if (this.elements.autoDetect) this.elements.autoDetect.addEventListener('change', () => this.handleAutoDetectChange());
      this.elements.paperInputs.forEach((input) => input.addEventListener('change', () => this.handlePaperChange()));
      this.elements.analyseButton.addEventListener('click', () => this.analyse());
      this.elements.generateButton.addEventListener('click', () => this.generate());
      this.elements.resetButton.addEventListener('click', () => this.reset());
      if (this.elements.preflightDetails) {
        this.elements.preflightDetails.addEventListener('toggle', () => {
          if (this.elements.preflightDetails.open) this.elements.preflightDetails.dataset.userOpened = 'true';
        });
      }
      this.elements.preflightFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.state.preflightFilter = button.dataset.filter || 'all';
          this.applyMatchFilter();
        });
      });
      if (this.elements.removeUnresolvedButton) {
        this.elements.removeUnresolvedButton.addEventListener('click', () => {
          let changed = false;
          this.state.matches.forEach((match) => {
            if (!match.excluded && match.packingPage == null) {
              match.excluded = true;
              changed = true;
            }
          });
          if (changed) this.invalidateGeneratedPreview();
          this.renderMatches();
        });
      }
      if (this.elements.restoreAllButton) {
        this.elements.restoreAllButton.addEventListener('click', () => {
          const changed = this.state.matches.some((match) => match.excluded);
          this.state.matches.forEach((match) => { match.excluded = false; });
          if (changed) this.invalidateGeneratedPreview();
          this.renderMatches();
        });
      }
      if (this.elements.matchPreviewModal) {
        this.elements.matchPreviewModal.querySelectorAll('[data-role="close-match-preview"]').forEach((button) => {
          button.addEventListener('click', () => this.closeMatchPreview());
        });
        this.elements.matchPreviewModal.addEventListener('click', (event) => {
          if (event.target === this.elements.matchPreviewModal) this.closeMatchPreview();
        });
        window.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && !this.elements.matchPreviewModal.hidden) this.closeMatchPreview();
        });
      }
      this.applyPaperProfile(this.getPaperProfile());
      this.updateProfileUi();
      this.updateDetectionUi();
      window.addEventListener('beforeunload', () => {
        if (this.state.ocrWorker) this.state.ocrWorker.terminate().catch(() => {});
        if (this.state.downloadUrl) URL.revokeObjectURL(this.state.downloadUrl);
      });
    }

    getSelectedCourierId() {
      return this.elements.courierInputs.find((input) => input.checked)?.value || 'evri';
    }

    getSelectedSalesChannelId() {
      return this.elements.salesChannelInputs.find((input) => input.checked)?.value || 'amazon';
    }

    getSelectedPaperId() {
      return this.elements.paperInputs.find((input) => input.checked)?.value || 'top_left_100x150';
    }

    getCourierProfile() {
      return COURIER_PROFILES[this.getSelectedCourierId()] || COURIER_PROFILES.evri;
    }

    getSalesChannelProfile() {
      return SALES_CHANNEL_PROFILES[this.getSelectedSalesChannelId()] || SALES_CHANNEL_PROFILES.amazon;
    }

    getPaperProfile() {
      return PAPER_PROFILES[this.getSelectedPaperId()] || PAPER_PROFILES.top_left_100x150;
    }

    applyPaperProfile(profile) {
      const layout = profile?.layout || PAPER_PROFILES.top_left_100x150.layout;
      const values = [
        ['labelX', layout.labelX],
        ['labelY', layout.labelY],
        ['labelWidth', layout.labelWidth],
        ['labelHeight', layout.labelHeight],
        ['packingX', layout.packingX],
        ['packingY', layout.packingY],
        ['packingWidth', layout.packingWidth],
        ['packingHeight', layout.packingHeight]
      ];
      values.forEach(([key, value]) => {
        if (this.elements[key]) this.elements[key].value = String(value);
      });
      if (this.elements.paperSummary) {
        this.elements.paperSummary.textContent = profile?.description || '';
      }
    }

    handlePaperChange() {
      const profile = this.getPaperProfile();
      this.applyPaperProfile(profile);
      this.updateProfileUi();
      this.elements.preview.hidden = true;
      if (this.state.downloadUrl) {
        URL.revokeObjectURL(this.state.downloadUrl);
        this.state.downloadUrl = null;
      }
      this.elements.previewFrame.removeAttribute('src');
      this.elements.download.removeAttribute('href');
      if (!this.elements.status.hidden) {
        this.showStatus(`Paper style changed to ${profile.label}. Create the PDF again to apply the new layout.`, 0, 'info');
      }
    }

    isAutoDetectEnabled() {
      return Boolean(this.elements.autoDetect?.checked);
    }

    setSelectedProfile(type, id) {
      const inputs = type === 'courier' ? this.elements.courierInputs : this.elements.salesChannelInputs;
      const target = inputs.find((input) => input.value === id);
      if (!target) return false;
      target.checked = true;
      return true;
    }

    handleAutoDetectChange() {
      this.state.detectedCourierId = '';
      this.state.detectedSalesChannelId = '';
      this.state.detectionComplete = false;
      this.state.shippingDetectionStatus = 'idle';
      this.state.packingDetectionStatus = 'idle';
      this.state.shippingDetectionToken += 1;
      this.state.packingDetectionToken += 1;
      this.hideReview();
      this.state.shippingRecords = [];
      this.state.packingRecords = [];
      this.state.matches = [];
      this.state.packingSafety = {};
      this.state.preflightFilter = 'all';
      if (this.elements.manualProfileDetails && !this.isAutoDetectEnabled()) {
        this.elements.manualProfileDetails.open = true;
      }
      this.updateProfileUi();
      this.updateDetectionUi();

      if (this.isAutoDetectEnabled()) {
        // If files are already selected, start identifying them immediately.
        if (this.state.shippingFile) this.queueUploadDetection('shipping', this.state.shippingFile);
        if (this.state.packingFile) this.queueUploadDetection('packing', this.state.packingFile);
        if (!this.state.shippingFile && !this.state.packingFile && !this.elements.status.hidden) {
          this.showStatus('Automatic detection enabled. Upload a PDF and we’ll identify it straight away.', 0, 'info');
        }
      } else if (!this.elements.status.hidden) {
        this.showStatus('Automatic detection turned off. Choose the courier and sales channel manually.', 0, 'info');
      }
      this.updateFileSummaries();
    }

    handleManualProfileChange() {
      if (this.elements.autoDetect?.checked) this.elements.autoDetect.checked = false;
      this.state.detectedCourierId = '';
      this.state.detectedSalesChannelId = '';
      this.state.detectionComplete = false;
      this.state.shippingDetectionStatus = 'idle';
      this.state.packingDetectionStatus = 'idle';
      this.state.shippingDetectionToken += 1;
      this.state.packingDetectionToken += 1;
      this.updateDetectionUi();
      this.handleProfileChange();
    }

    updateDetectionUi() {
      if (!this.elements.detectionSummary) return;
      const automatic = this.isAutoDetectEnabled();
      this.elements.detectionSummary.dataset.mode = automatic ? 'automatic' : 'manual';

      if (automatic) {
        const statusLabel = (type) => {
          const isShipping = type === 'shipping';
          const id = isShipping ? this.state.detectedCourierId : this.state.detectedSalesChannelId;
          const status = isShipping ? this.state.shippingDetectionStatus : this.state.packingDetectionStatus;
          const file = isShipping ? this.state.shippingFile : this.state.packingFile;
          const profiles = isShipping ? COURIER_PROFILES : SALES_CHANNEL_PROFILES;

          if (status === 'detecting') return 'Detecting…';
          if (id) return profiles[id]?.label || id;
          if (status === 'failed') return 'Choose manually';
          return file ? 'Waiting to detect' : 'Upload a PDF';
        };

        if (this.elements.detectedCourier) this.elements.detectedCourier.textContent = statusLabel('shipping');
        if (this.elements.detectedChannel) this.elements.detectedChannel.textContent = statusLabel('packing');
      } else {
        if (this.elements.detectedCourier) this.elements.detectedCourier.textContent = this.getCourierProfile().label;
        if (this.elements.detectedChannel) this.elements.detectedChannel.textContent = this.getSalesChannelProfile().label;
      }
    }

    isUploadDetectionBusy() {
      return this.state.shippingDetectionStatus === 'detecting' || this.state.packingDetectionStatus === 'detecting';
    }

    queueUploadDetection(type, file) {
      if (!this.isAutoDetectEnabled() || !file) return;
      const tokenKey = type === 'shipping' ? 'shippingDetectionToken' : 'packingDetectionToken';
      this.state[tokenKey] += 1;
      const token = this.state[tokenKey];
      const statusKey = type === 'shipping' ? 'shippingDetectionStatus' : 'packingDetectionStatus';
      this.state[statusKey] = 'detecting';
      this.updateDetectionUi();
      this.updateFileSummaries();

      this.detectionQueue = this.detectionQueue
        .catch(() => {})
        .then(() => this.detectUploadedFile(type, file, token))
        .catch((error) => {
          console.warn(`Automatic ${type} PDF detection failed.`, error);
        });
    }

    async detectUploadedFile(type, file, token) {
      const tokenKey = type === 'shipping' ? 'shippingDetectionToken' : 'packingDetectionToken';
      const statusKey = type === 'shipping' ? 'shippingDetectionStatus' : 'packingDetectionStatus';
      const currentFileKey = type === 'shipping' ? 'shippingFile' : 'packingFile';
      const detectedKey = type === 'shipping' ? 'detectedCourierId' : 'detectedSalesChannelId';

      if (this.state[tokenKey] !== token || this.state[currentFileKey] !== file || !this.isAutoDetectEnabled()) return;

      try {
        await this.ensurePdfLibraries();
        if (this.state[tokenKey] !== token || this.state[currentFileKey] !== file || !this.isAutoDetectEnabled()) return;

        const bytes = new Uint8Array(await file.arrayBuffer());
        const pdf = await window.pdfjsLib.getDocument({ data: bytes.slice() }).promise;
        if (this.state[tokenKey] !== token || this.state[currentFileKey] !== file || !this.isAutoDetectEnabled()) return;

        if (type === 'shipping') {
          this.state.shippingBytes = bytes;
          this.state.shippingPdf = pdf;
        } else {
          this.state.packingBytes = bytes;
          this.state.packingPdf = pdf;
        }

        const detected = await this.detectPdfProfile(pdf, type);
        if (this.state[tokenKey] !== token || this.state[currentFileKey] !== file || !this.isAutoDetectEnabled()) return;

        if (!detected) {
          this.state[detectedKey] = '';
          this.state[statusKey] = 'failed';
          this.state.detectionComplete = false;
          if (this.elements.manualProfileDetails) this.elements.manualProfileDetails.open = true;
        } else {
          const profileType = type === 'shipping' ? 'courier' : 'channel';
          this.setSelectedProfile(profileType, detected.id);
          this.state[detectedKey] = detected.id;
          this.state[statusKey] = 'detected';
          this.state.detectionComplete = Boolean(this.state.detectedCourierId && this.state.detectedSalesChannelId);
        }

        this.updateProfileUi();
        this.updateDetectionUi();

        // Detection happens as part of upload now, so the small summary boxes are
        // the result. Do not leave a large OCR/progress message visible afterwards.
        if (!this.state.isBusy) this.elements.status.hidden = true;
      } catch (error) {
        if (this.state[tokenKey] !== token || this.state[currentFileKey] !== file) return;
        this.state[detectedKey] = '';
        this.state[statusKey] = 'failed';
        this.state.detectionComplete = false;
        this.updateDetectionUi();
        if (this.elements.manualProfileDetails) this.elements.manualProfileDetails.open = true;
        if (!this.state.isBusy) this.elements.status.hidden = true;
        console.warn(`Could not automatically identify the ${type} PDF.`, error);
      } finally {
        if (this.state[tokenKey] === token && this.state[currentFileKey] === file && this.state[statusKey] === 'detecting') {
          this.state[statusKey] = this.state[detectedKey] ? 'detected' : 'failed';
          this.state.detectionComplete = Boolean(this.state.detectedCourierId && this.state.detectedSalesChannelId);
          this.updateDetectionUi();
        }
        this.updateFileSummaries();
      }
    }

    async detectPdfProfile(pdf, type) {
      const detector = type === 'shipping' ? detectCourierFromText : detectSalesChannelFromText;
      const pageCount = Math.min(2, pdf.numPages);
      let combinedText = '';
      for (let index = 0; index < pageCount; index += 1) {
        const page = await pdf.getPage(index + 1);
        combinedText += `\n${await extractPdfText(page)}`;
      }

      let detected = detector(combinedText);
      if (detected?.confidence === 'high') return detected;

      // Image-only labels and some marketplace PDFs use custom font encoding,
      // so their visible text may not exist in the PDF text layer. OCR only the
      // first page when native text is not decisive; this keeps auto-detection
      // fast for normal PDFs while still covering scanned/garbled documents.
      this.showStatus(`Detecting ${type === 'shipping' ? 'courier' : 'sales channel'}...`, type === 'shipping' ? 3 : 6, 'info');
      const canvas = await renderPdfPage(pdf, 0, 1250);
      const ocrResult = await this.recogniseCanvas(canvas, false);
      combinedText += `\n${ocrResult.text || ''}`;
      detected = detector(combinedText);
      return detected;
    }

    async autoDetectProfiles() {
      if (!this.isAutoDetectEnabled()) {
        this.state.detectionComplete = false;
        this.updateDetectionUi();
        return;
      }

      // Upload-time detection is the normal path. Analyse only fills in anything
      // that could not be completed earlier (for example after a transient OCR load).
      let courierDetection = this.state.detectedCourierId
        ? { id: this.state.detectedCourierId, confidence: 'high' }
        : null;
      let channelDetection = this.state.detectedSalesChannelId
        ? { id: this.state.detectedSalesChannelId, confidence: 'high' }
        : null;

      if (!courierDetection) {
        this.showStatus('Identifying the shipping-label PDF...', 2, 'info');
        courierDetection = await this.detectPdfProfile(this.state.shippingPdf, 'shipping');
      }
      if (!channelDetection) {
        this.showStatus('Identifying the packing-slip PDF...', 5, 'info');
        channelDetection = await this.detectPdfProfile(this.state.packingPdf, 'packing');
      }

      if (!courierDetection || !channelDetection) {
        if (this.elements.manualProfileDetails) this.elements.manualProfileDetails.open = true;
        const missing = [
          !courierDetection ? 'courier' : '',
          !channelDetection ? 'sales channel' : ''
        ].filter(Boolean).join(' and ');
        throw new Error(`Automatic detection could not confidently identify the ${missing}. Open “Wrong detection? Change it manually”, select the correct option, and analyse again.`);
      }

      this.setSelectedProfile('courier', courierDetection.id);
      this.setSelectedProfile('channel', channelDetection.id);
      this.state.detectedCourierId = courierDetection.id;
      this.state.detectedSalesChannelId = channelDetection.id;
      this.state.shippingDetectionStatus = 'detected';
      this.state.packingDetectionStatus = 'detected';
      this.state.detectionComplete = true;
      this.updateProfileUi();
      this.updateDetectionUi();
      this.showStatus(`Detected ${SALES_CHANNEL_PROFILES[channelDetection.id].label} + ${COURIER_PROFILES[courierDetection.id].label}. Matching orders...`, 7, 'info');
    }

    handleProfileChange() {
      this.updateProfileUi();
      this.hideReview();
      this.state.shippingRecords = [];
      this.state.packingRecords = [];
      this.state.matches = [];
      this.state.packingSafety = {};
      this.state.preflightFilter = 'all';
      if (!this.elements.status.hidden) {
        this.showStatus('Courier or sales channel changed. Analyse the PDFs again.', 0, 'info');
      }
    }

    updateProfileUi() {
      const courier = this.getCourierProfile();
      const channel = this.getSalesChannelProfile();
      const paper = this.getPaperProfile();
      if (this.isAutoDetectEnabled() && !this.state.detectionComplete) {
        if (this.elements.shippingTitle) this.elements.shippingTitle.textContent = 'Shipping-label PDF';
        if (this.elements.shippingHelp) this.elements.shippingHelp.textContent = 'Upload the courier shipping-label PDF. We’ll detect the courier automatically.';
        if (this.elements.packingTitle) this.elements.packingTitle.textContent = 'Packing-slip PDF';
        if (this.elements.packingHelp) this.elements.packingHelp.textContent = 'Upload the marketplace packing-slip/order PDF. We’ll detect the sales channel automatically.';
      } else {
        if (this.elements.shippingTitle) this.elements.shippingTitle.textContent = courier.uploadTitle;
        if (this.elements.shippingHelp) this.elements.shippingHelp.textContent = courier.uploadHelp;
        if (this.elements.packingTitle) this.elements.packingTitle.textContent = channel.packingTitle;
        if (this.elements.packingHelp) this.elements.packingHelp.textContent = channel.packingHelp;
      }
      if (this.elements.modeSummary) {
        this.elements.modeSummary.textContent = `${channel.label} orders with ${courier.label} labels on ${paper.shortLabel} paper`;
      }
      if (this.elements.paperSummary) {
        this.elements.paperSummary.textContent = paper.description;
      }
      // Legacy duplicate safety banner is no longer used. The pre-flight panel
      // is the single source of truth for packing-slip safety problems.
      if (this.elements.fullSlipWarning) {
        this.elements.fullSlipWarning.hidden = true;
      }
    }

    handleFile(type) {
      const input = type === 'shipping' ? this.elements.shippingInput : this.elements.packingInput;
      const file = input.files?.[0] || null;
      if (file && file.size > MAX_FILE_BYTES) {
        this.showError(`${file.name} is larger than 75 MB. Please split the PDF into smaller batches.`);
        input.value = '';
        return;
      }
      if (file && !file.name.toLowerCase().endsWith('.pdf')) {
        this.showError('Please choose a PDF file.');
        input.value = '';
        return;
      }

      if (type === 'shipping') {
        this.state.shippingFile = file;
        this.state.shippingBytes = null;
        this.state.shippingPdf = null;
        this.state.detectedCourierId = '';
        this.state.shippingDetectionStatus = file ? 'idle' : 'idle';
        this.state.shippingDetectionToken += 1;
      } else {
        this.state.packingFile = file;
        this.state.packingBytes = null;
        this.state.packingPdf = null;
        this.state.detectedSalesChannelId = '';
        this.state.packingDetectionStatus = file ? 'idle' : 'idle';
        this.state.packingDetectionToken += 1;
      }

      this.state.detectionComplete = Boolean(this.state.detectedCourierId && this.state.detectedSalesChannelId);
      this.updateProfileUi();
      this.updateDetectionUi();
      this.updateFileSummaries();
      this.hideReview();

      // v1.37.1: identify each document as soon as it is selected rather than
      // waiting for the customer to press Analyse and match orders.
      if (this.isAutoDetectEnabled() && file) {
        this.queueUploadDetection(type, file);
      }
    }

    updateFileSummaries() {
      const format = (file) => file ? `${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)` : 'No PDF selected';
      this.elements.shippingSummary.textContent = format(this.state.shippingFile);
      this.elements.packingSummary.textContent = format(this.state.packingFile);
      this.elements.analyseButton.disabled = !(this.state.shippingFile && this.state.packingFile) || this.state.isBusy || this.isUploadDetectionBusy();
    }

    setBusy(value) {
      this.state.isBusy = value;
      this.root.classList.toggle('ail-is-busy', value);
      this.elements.analyseButton.disabled = value || this.isUploadDetectionBusy() || !(this.state.shippingFile && this.state.packingFile);
      this.elements.generateButton.disabled = value || !this.getPreflightReport().ready;
      this.elements.resetButton.disabled = value;
    }

    showStatus(message, progress = null, kind = 'info') {
      this.elements.status.hidden = false;
      this.elements.status.dataset.kind = kind;
      this.elements.statusText.textContent = message;
      if (progress == null) {
        this.elements.progress.removeAttribute('value');
      } else {
        this.elements.progress.value = clamp(progress, 0, 100);
      }
    }

    showError(message) {
      this.showStatus(message, 100, 'error');
    }

    showGenerationProgress(message, progress = 0, kind = 'info') {
      if (!this.elements.generationProgress) return;
      const value = clamp(Number(progress) || 0, 0, 100);
      this.elements.generationProgress.hidden = false;
      this.elements.generationProgress.dataset.kind = kind;
      if (this.elements.generationProgressText) this.elements.generationProgressText.textContent = message;
      if (this.elements.generationProgressPercent) this.elements.generationProgressPercent.textContent = `${Math.round(value)}%`;
      if (this.elements.generationProgressBar) this.elements.generationProgressBar.value = value;
    }

    resetGenerationProgress() {
      if (!this.elements.generationProgress) return;
      this.elements.generationProgress.hidden = true;
      this.elements.generationProgress.dataset.kind = 'info';
      if (this.elements.generationProgressText) this.elements.generationProgressText.textContent = 'Ready to create PDF';
      if (this.elements.generationProgressPercent) this.elements.generationProgressPercent.textContent = '0%';
      if (this.elements.generationProgressBar) this.elements.generationProgressBar.value = 0;
    }

    hideReview() {
      this.elements.review.hidden = true;
      this.elements.preview.hidden = true;
      this.resetGenerationProgress();
      if (this.state.downloadUrl) {
        URL.revokeObjectURL(this.state.downloadUrl);
        this.state.downloadUrl = null;
      }
      this.elements.previewFrame.removeAttribute('src');
      this.elements.download.removeAttribute('href');
    }


    invalidateGeneratedPreview() {
      this.elements.preview.hidden = true;
      this.resetGenerationProgress();
      if (this.state.downloadUrl) {
        URL.revokeObjectURL(this.state.downloadUrl);
        this.state.downloadUrl = null;
      }
      this.elements.previewFrame.removeAttribute('src');
      this.elements.download.removeAttribute('href');
    }

    getPreflightReport() {
      return buildPreflightReport(
        this.state.shippingRecords,
        this.state.packingRecords,
        this.state.matches,
        this.state.packingSafety
      );
    }

    getMatchPreflightIssues(match, report = this.getPreflightReport()) {
      if (!match || match.excluded) return [];
      const issues = [];
      if (match.packingPage == null) issues.push('No packing-slip match');
      if (match.packingPage != null && match.confidence !== 'High') {
        issues.push(`${match.confidence} confidence — review`);
      }
      if (match.packingPage != null && this.state.packingSafety?.[match.packingPage]?.safe === false) {
        issues.push('Product table failed privacy check');
      }
      if (match.packingPage != null && !this.state.packingSafety?.[match.packingPage]) {
        issues.push('Product table check pending');
      }
      if (report.duplicateAssignments.some((group) => (
        group.packingPage === Number(match.packingPage) &&
        group.shippingPages.includes(Number(match.shippingPage))
      ))) {
        issues.push('Packing-slip page used more than once');
      }
      const shippingRecord = this.state.shippingRecords[match.shippingPage];
      const duplicateTracking = report.duplicateShippingTracking.some((group) => (
        (shippingRecord?.trackingIds || []).includes(group.value)
      ));
      if (duplicateTracking) issues.push('Duplicate shipping tracking ID');
      return issues;
    }

    renderPreflight() {
      if (!this.elements.preflightPanel) return;
      const report = this.getPreflightReport();

      // Never show a temporary red failure while OCR/privacy checks are still
      // running. Pending safety checks are an internal processing state, not a
      // customer problem. Only present Ready / Check / Fix required once the
      // current round of checks has finished.
      if (this.state.preflightChecking) {
        this.elements.preflightPanel.dataset.kind = 'checking';
        if (this.elements.preflightBadge) this.elements.preflightBadge.textContent = 'Checking';
        if (this.elements.preflightReadiness) this.elements.preflightReadiness.textContent = 'Checking your orders...';
        if (this.elements.preflightSubtext) this.elements.preflightSubtext.textContent = 'Please wait while we finish matching and verify the packing slips.';
        if (this.elements.preflightDetails) {
          this.elements.preflightDetails.open = false;
          this.elements.preflightDetails.hidden = true;
        }
        if (this.elements.removeUnresolvedButton) this.elements.removeUnresolvedButton.disabled = true;
        if (this.elements.restoreAllButton) this.elements.restoreAllButton.disabled = true;
        this.elements.generateButton.disabled = true;
        return;
      }

      if (this.elements.preflightDetails) this.elements.preflightDetails.hidden = false;
      const badgeKind = report.ready ? 'ready' : (report.blockers.length ? 'blocked' : 'review');
      this.elements.preflightPanel.dataset.kind = badgeKind;

      const warningCount = report.warnings.length;
      const attentionCount = report.blockers.length + report.reviewCount;

      if (this.elements.preflightBadge) {
        this.elements.preflightBadge.textContent = report.blockers.length
          ? 'Fix required'
          : (attentionCount || warningCount ? 'Check' : 'Ready');
      }

      if (this.elements.preflightReadiness) {
        if (report.blockers.length) {
          this.elements.preflightReadiness.textContent = `${report.blockers.length} issue${report.blockers.length === 1 ? '' : 's'} need attention`;
        } else if (report.ready) {
          this.elements.preflightReadiness.textContent = `${report.includedCount} order${report.includedCount === 1 ? '' : 's'} ready to create`;
        } else {
          this.elements.preflightReadiness.textContent = 'Check your orders before continuing';
        }
      }

      if (this.elements.preflightSubtext) {
        if (report.blockers.length) {
          this.elements.preflightSubtext.textContent = 'Fix the highlighted order or remove it for manual printing.';
        } else if (report.reviewCount) {
          this.elements.preflightSubtext.textContent = `${report.reviewCount} match${report.reviewCount === 1 ? '' : 'es'} worth a quick check before printing.`;
        } else if (warningCount) {
          this.elements.preflightSubtext.textContent = `${warningCount} non-blocking check${warningCount === 1 ? '' : 's'} available in details.`;
        } else {
          this.elements.preflightSubtext.textContent = 'Everything looks good.';
        }
      }

      if (this.elements.preflightMetrics) {
        const metrics = [
          ['Labels', report.shippingCount],
          ['Packing slips', report.packingCount],
          ['Ready', report.includedCount - report.unmatchedCount],
          ['Removed', report.removedCount]
        ];
        this.elements.preflightMetrics.innerHTML = metrics.map(([label, value]) => `
          <div class="ail-preflight-metric">
            <strong>${escapeHtml(value)}</strong>
            <span>${escapeHtml(label)}</span>
          </div>
        `).join('');
      }

      if (this.elements.preflightIssues) {
        const issueRows = [
          ...report.blockers.map((issue) => ({ ...issue, kind: 'error' })),
          ...report.warnings.map((issue) => ({ ...issue, kind: 'warning' }))
        ];
        this.elements.preflightIssues.innerHTML = issueRows.length
          ? issueRows.map((issue) => `
              <li data-kind="${issue.kind}">
                <span class="ail-preflight-issue-icon" aria-hidden="true">${issue.kind === 'error' ? '!' : 'i'}</span>
                <span>${escapeHtml(issue.message)}</span>
              </li>
            `).join('')
          : '<li data-kind="success"><span class="ail-preflight-issue-icon" aria-hidden="true">✓</span><span>No problems found.</span></li>';
      }

      if (this.elements.preflightDetails) {
        // Keep the normal successful flow clean. Only force the technical details
        // open when the user actually has a blocking problem to resolve.
        if (report.blockers.length) this.elements.preflightDetails.open = true;
        if (!report.blockers.length && report.ready && !this.elements.preflightDetails.dataset.userOpened) {
          this.elements.preflightDetails.open = false;
        }
      }

      if (this.elements.removeUnresolvedButton) {
        this.elements.removeUnresolvedButton.disabled = report.unmatchedCount === 0 || this.state.isBusy;
      }
      if (this.elements.restoreAllButton) {
        this.elements.restoreAllButton.disabled = report.removedCount === 0 || this.state.isBusy;
      }
      this.elements.generateButton.disabled = this.state.isBusy || !report.ready;
    }

    applyMatchFilter() {
      const filter = this.state.preflightFilter || 'all';
      const report = this.getPreflightReport();
      this.elements.preflightFilterButtons.forEach((button) => {
        const selected = (button.dataset.filter || 'all') === filter;
        button.classList.toggle('ail-filter-button--active', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      this.elements.matchBody.querySelectorAll('tr[data-shipping-page]').forEach((row) => {
        const shippingPage = Number(row.dataset.shippingPage);
        const match = this.state.matches.find((item) => item.shippingPage === shippingPage);
        if (!match) {
          row.hidden = false;
          return;
        }
        const issues = this.getMatchPreflightIssues(match, report);
        let show = true;
        if (filter === 'problems') show = !match.excluded && issues.length > 0;
        if (filter === 'unmatched') show = !match.excluded && match.packingPage == null;
        row.hidden = !show;
      });
    }

    async validatePackingSafety(pageIndexes = null) {
      const selectedChannel = this.getSelectedSalesChannelId();
      const candidatePages = pageIndexes
        ? pageIndexes
        : this.state.matches
            .filter((match) => !match.excluded && match.packingPage != null)
            .map((match) => Number(match.packingPage));
      const uniquePages = [...new Set(candidatePages.filter((page) => Number.isInteger(page) && page >= 0))];

      for (let index = 0; index < uniquePages.length; index += 1) {
        const pageIndex = uniquePages[index];
        if (this.state.packingSafety?.[pageIndex]?.safe === true) continue;
        const overallProgress = 86 + ((index / Math.max(1, uniquePages.length)) * 12);
        this.showStatus(
          `Pre-flight privacy check: packing-slip page ${pageIndex + 1} (${index + 1} of ${uniquePages.length})...`,
          overallProgress,
          'info'
        );
        try {
          const canvas = await renderPdfPage(this.state.packingPdf, pageIndex, 1550);
          const ocrBoxes = await this.ensurePackingLayoutOcr(pageIndex, canvas);
          const bounds = await findPackingProductBounds(
            this.state.packingPdf,
            pageIndex,
            canvas,
            ocrBoxes,
            selectedChannel
          );
          this.state.packingSafety[pageIndex] = {
            safe: Boolean(bounds),
            checkedAt: Date.now()
          };
        } catch (error) {
          console.warn(`Pre-flight product-table check failed for packing-slip page ${pageIndex + 1}.`, error);
          this.state.packingSafety[pageIndex] = {
            safe: false,
            reason: error?.message || 'Product table could not be checked safely.',
            checkedAt: Date.now()
          };
        }
      }
    }

    async ensurePdfLibraries() {
      this.showStatus('Loading the PDF tools...', 3);
      await Promise.all([
        loadScript(CDN.pdfjs, 'pdfjsLib'),
        loadScript(CDN.pdfLib, 'PDFLib')
      ]);
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = CDN.pdfWorker;
    }

    async ensureOcrWorker() {
      if (this.state.ocrWorker) return this.state.ocrWorker;
      this.showStatus('Loading the local OCR engine for the first label...', 10);
      await loadScript(CDN.tesseract, 'Tesseract');
      this.state.ocrWorker = await window.Tesseract.createWorker('eng', 1, {
        logger: (message) => {
          if (typeof message.progress === 'number') {
            const percent = 12 + (message.progress * 18);
            this.showStatus(`OCR: ${message.status || 'reading label'}...`, percent);
          }
        }
      });
      await this.state.ocrWorker.setParameters({
        preserve_interword_spaces: '1',
        user_defined_dpi: '300'
      });
      return this.state.ocrWorker;
    }

    async recogniseCanvas(canvas, includeBoxes = false) {
      const worker = await this.ensureOcrWorker();
      const output = includeBoxes ? { text: true, tsv: true } : { text: true };
      const result = await worker.recognize(canvas, {}, output);
      return {
        text: result?.data?.text || '',
        boxes: includeBoxes ? parseTesseractTsv(result?.data?.tsv || '', canvas) : []
      };
    }


    async recogniseAmazonEvriOrderNumber(pdf, pageIndex) {
      // Amazon marketplace EVRi PDFs are often image-only. The order reference is
      // printed in the small SKU row near the bottom-left of the label. Different
      // EVRi exports move that row slightly, and OCR may remove the hyphens, so use
      // a tight first pass and a broader second pass only when necessary.
      const canvas = await renderPdfPage(pdf, pageIndex, 2400, 4.6);
      const tightCrop = cropCanvas(
        canvas,
        canvas.width * 0.04,
        canvas.height * 0.70,
        canvas.width * 0.66,
        canvas.height * 0.18
      );
      const first = await this.recogniseCanvas(tightCrop, false);
      let combinedText = first.text || '';
      let orderIds = [...new Set([
        ...extractOrderIds(combinedText),
        ...extractNoisyAmazonOrderIds(combinedText)
      ])];

      if (!orderIds.length) {
        const broadCrop = cropCanvas(
          canvas,
          canvas.width * 0.02,
          canvas.height * 0.58,
          canvas.width * 0.74,
          canvas.height * 0.32
        );
        const second = await this.recogniseCanvas(broadCrop, false);
        combinedText += `\n${second.text || ''}`;
        orderIds = [...new Set([
          ...extractOrderIds(combinedText),
          ...extractNoisyAmazonOrderIds(combinedText)
        ])];
      }

      return { text: combinedText, orderIds };
    }


    async recogniseTemuTrackingNumber(pdf, pageIndex, type) {
      // Some Temu packing-slip PDFs have a deliberately unusable/garbled PDF
      // text layer even though the tracking number is perfectly visible on the
      // rendered page. Matching Temu by order/postcode is not sufficient: the
      // H tracking number is the authoritative cross-document identifier.
      // Read the small region where the tracking reference is printed at a much
      // higher resolution before giving up on the page.
      const canvas = await renderPdfPage(pdf, pageIndex, 2400, 4.8);
      let primaryCrop;
      if (type === 'packing') {
        // Temu packing slips print "Tracking Number" across the top edge.
        primaryCrop = cropCanvas(
          canvas,
          0,
          0,
          canvas.width,
          canvas.height * 0.24
        );
      } else if (this.getSelectedCourierId() === 'evri') {
        // EVRi TEMU-Fulfilment labels print the H-055B-A reference below the
        // lower barcode.
        primaryCrop = cropCanvas(
          canvas,
          canvas.width * 0.02,
          canvas.height * 0.68,
          canvas.width * 0.80,
          canvas.height * 0.30
        );
      } else {
        primaryCrop = canvas;
      }

      const first = await this.recogniseCanvas(primaryCrop, false);
      let combinedText = first.text || '';
      let trackingIds = extractTrackingIds(combinedText, 'temu');

      if (!trackingIds.length && primaryCrop !== canvas) {
        const second = await this.recogniseCanvas(canvas, false);
        combinedText += `\n${second.text || ''}`;
        trackingIds = extractTrackingIds(combinedText, 'temu');
      }

      return { text: combinedText, trackingIds };
    }

    async ensurePackingLayoutOcr(pageIndex, canvas) {
      const record = this.state.packingRecords[pageIndex];
      if (!record) return [];

      const pdfBoxes = await getPdfTextBoxes(this.state.packingPdf, pageIndex, canvas);
      const salesChannel = record.salesChannel || this.getSelectedSalesChannelId();
      if (findPackingTableHeader(groupTextBoxesIntoRows(pdfBoxes), salesChannel)) {
        return Array.isArray(record.ocrBoxes) ? record.ocrBoxes : [];
      }

      const existing = Array.isArray(record.ocrBoxes) ? record.ocrBoxes : [];
      if (existing.length) {
        const scaled = scaleNormalisedBoxes(existing, canvas);
        if (findPackingTableHeader(groupTextBoxesIntoRows(scaled), salesChannel)) return existing;
      }

      this.showStatus(`Locating the privacy-safe product table on packing-slip page ${pageIndex + 1}...`, null);
      const ocrResult = await this.recogniseCanvas(canvas, true);
      record.ocrBoxes = ocrResult.boxes || [];
      if (ocrResult.text) {
        record.rawText = `${record.rawText || ''}
${ocrResult.text}`;
        record.postcodes = [...new Set([...(record.postcodes || []), ...extractPostcodes(ocrResult.text)])];
        record.orderIds = [...new Set([...(record.orderIds || []), ...extractChannelOrderIds(ocrResult.text, record.salesChannel || this.getSelectedSalesChannelId())])];
        record.trackingIds = [...new Set([...(record.trackingIds || []), ...extractTrackingIds(ocrResult.text, record.salesChannel || this.getSelectedSalesChannelId())])];
      }
      return record.ocrBoxes;
    }

    async analysePdf(pdf, type, totalProgressStart, totalProgressSpan) {
      if (pdf.numPages > MAX_PAGES) throw new Error(`The ${type} PDF contains more than ${MAX_PAGES} pages. Please process it in smaller batches.`);
      const records = [];
      for (let index = 0; index < pdf.numPages; index += 1) {
        const progress = totalProgressStart + ((index / Math.max(1, pdf.numPages)) * totalProgressSpan);
        this.showStatus(`Reading ${type === 'shipping' ? this.getCourierProfile().label + ' label' : this.getSalesChannelProfile().label + ' packing slip'} ${index + 1} of ${pdf.numPages}...`, progress);
        const page = await pdf.getPage(index + 1);
        let text = await extractPdfText(page);
        let ocrBoxes = [];
        let refinedAmazonEvriOrderIds = [];
        const salesChannel = this.getSelectedSalesChannelId();
        const nativeOrderIds = extractChannelOrderIds(text, salesChannel);
        const nativeTrackingIds = extractTrackingIds(text, salesChannel);
        // TikTok packing pages are matched by page order and therefore do not
        // need OCR merely to discover a matching identifier. Temu is different:
        // tracking number is the authoritative identifier, so an incidental
        // postcode/order-like string in a damaged PDF text layer must never stop
        // OCR from reading the visible H tracking reference.
        const pageOrderOnlyPacking = salesChannel === 'tiktok' && type === 'packing';
        const identifiersFound = salesChannel === 'temu'
          ? nativeTrackingIds.length > 0
          : (pageOrderOnlyPacking || nativeOrderIds.length || extractPostcodes(text).length);

        if (!identifiersFound) {
          const canvas = await renderPdfPage(pdf, index, type === 'shipping' ? 1500 : 1250);
          let ocrCanvas = canvas;
          const courierProfile = this.getCourierProfile();
          if (type === 'shipping' && courierProfile.ocrCrop) {
            const crop = courierProfile.ocrCrop;
            ocrCanvas = cropCanvas(
              canvas,
              canvas.width * crop.x,
              canvas.height * crop.y,
              canvas.width * crop.width,
              canvas.height * crop.height
            );
          }
          const ocrResult = await this.recogniseCanvas(ocrCanvas, type === 'packing');
          text += `
${ocrResult.text}`;
          if (type === 'packing') ocrBoxes = ocrResult.boxes;
          if (type === 'shipping' && !extractChannelOrderIds(text, salesChannel).length && !extractPostcodes(text).length && ocrCanvas !== canvas) {
            const fullResult = await this.recogniseCanvas(canvas, false);
            text += `
${fullResult.text}`;
          }
        }

        // If normal text/full-page OCR still did not yield a Temu tracking
        // identifier, perform a focused high-resolution read of the tracking
        // region. This specifically covers image-visible Temu slips whose PDF
        // text layer is scrambled by custom font encoding.
        const shouldRefineTemuTracking = salesChannel === 'temu'
          && extractTrackingIds(text, salesChannel).length === 0;
        if (shouldRefineTemuTracking) {
          this.showStatus(`Double-checking Temu tracking number on ${type === 'shipping' ? 'label' : 'packing-slip'} page ${index + 1}...`, progress);
          const refinedTemu = await this.recogniseTemuTrackingNumber(pdf, index, type);
          if (refinedTemu.text) text += `
${refinedTemu.text}`;
        }

        const shouldRefineAmazonEvriOrder = type === 'shipping'
          && salesChannel === 'amazon'
          && this.getSelectedCourierId() === 'evri'
          && nativeOrderIds.length === 0;
        if (shouldRefineAmazonEvriOrder) {
          this.showStatus(`Double-checking Amazon order number on EVRi label ${index + 1}...`, progress);
          const refined = await this.recogniseAmazonEvriOrderNumber(pdf, index);
          refinedAmazonEvriOrderIds = refined.orderIds || [];
          if (refined.text) text += `
${refined.text}`;
        }

        const record = parseIdentity(text, index, type, { salesChannel, courierId: type === 'shipping' ? this.getSelectedCourierId() : '' });
        if (refinedAmazonEvriOrderIds.length) {
          // Prefer the high-resolution SKU-row result and discard one-character
          // variants produced by the broader OCR pass. This keeps the review table
          // clear and prevents a low-quality OCR variant from becoming a candidate.
          const refinedSet = new Set(refinedAmazonEvriOrderIds.map(normaliseOrderId));
          const keep = (orderId) => {
            const normalised = normaliseOrderId(orderId);
            if (refinedSet.has(normalised)) return false;
            const digits = amazonOrderDigits(normalised);
            if (!digits) return true;
            return !refinedAmazonEvriOrderIds.some((refinedId) => {
              const refinedDigits = amazonOrderDigits(refinedId);
              return refinedDigits && levenshtein(digits, refinedDigits) <= 1;
            });
          };
          record.orderIds = [...new Set([
            ...refinedAmazonEvriOrderIds.map(normaliseOrderId),
            ...record.orderIds.filter(keep)
          ])];
        }
        record.ocrBoxes = ocrBoxes;
        records.push(record);
      }
      return records;
    }

    async analyse() {
      if (!this.state.shippingFile || !this.state.packingFile || this.state.isBusy || this.isUploadDetectionBusy()) return;
      this.setBusy(true);
      this.hideReview();
      try {
        await this.ensurePdfLibraries();
        if (!this.state.shippingBytes || !this.state.shippingPdf) {
          this.state.shippingBytes = new Uint8Array(await this.state.shippingFile.arrayBuffer());
          this.state.shippingPdf = await window.pdfjsLib.getDocument({ data: this.state.shippingBytes.slice() }).promise;
        }
        if (!this.state.packingBytes || !this.state.packingPdf) {
          this.state.packingBytes = new Uint8Array(await this.state.packingFile.arrayBuffer());
          this.state.packingPdf = await window.pdfjsLib.getDocument({ data: this.state.packingBytes.slice() }).promise;
        }

        await this.autoDetectProfiles();

        this.state.shippingRecords = await this.analysePdf(this.state.shippingPdf, 'shipping', 8, 44);
        this.state.packingRecords = await this.analysePdf(this.state.packingPdf, 'packing', 53, 32);

        // EVRi marketplace labels are image-only and the tiny SKU/order-number
        // row can occasionally OCR one digit incorrectly even though the printed
        // label is clear. Once the Amazon packing slips are available, reconcile
        // a unique one-digit OCR variant when the recipient independently agrees.
        if (this.getSelectedSalesChannelId() === 'amazon' && this.getSelectedCourierId() === 'evri') {
          reconcileAmazonEvriOcrOrderIds(this.state.shippingRecords, this.state.packingRecords);
        }

        // Amazon Shipping courier-label PDFs can look superficially similar to a
        // compact Amazon packing document, especially when they are image-only.
        // Do not accept a courier label in the packing-slip slot: it contains no
        // product/SKU/quantity table and cannot produce a useful privacy-safe
        // packing section. Give the user a specific correction instead of the
        // generic "product table could not be located" message later on.
        if (this.getSelectedSalesChannelId() === 'amazon') {
          const courierLikePackingPages = this.state.packingRecords.filter((record) =>
            looksLikeAmazonShippingCourierLabel(record.rawText)
          );
          if (courierLikePackingPages.length && courierLikePackingPages.length >= Math.ceil(this.state.packingRecords.length / 2)) {
            throw new Error(
              'The PDF selected as the Amazon packing slip appears to be an Amazon Shipping courier-label PDF. ' +
              'Please upload this file in the Shipping label box and upload the Amazon packing slip/order document in the Packing-slip box.'
            );
          }
        }

        this.state.matches = matchPages(
          this.state.shippingRecords,
          this.state.packingRecords,
          this.elements.fallback.checked
        ).map((match) => ({ ...match, excluded: false }));
        this.state.packingSafety = {};
        this.state.preflightFilter = 'all';

        // Keep the review area hidden until all automatic checks have finished.
        // Previously the review panel rendered while packing-slip safety checks
        // were still pending, which briefly looked like a red failure even when
        // the batch ultimately passed.
        this.state.preflightChecking = true;
        await this.validatePackingSafety();
        this.state.preflightChecking = false;
        this.elements.review.hidden = false;
        this.renderMatches();

        const report = this.getPreflightReport();
        const message = report.blockers.length
          ? `${report.blockers.length} order issue${report.blockers.length === 1 ? '' : 's'} need attention before creating the PDF.`
          : (report.reviewCount
            ? `${report.includedCount} orders ready. ${report.reviewCount} match${report.reviewCount === 1 ? '' : 'es'} worth checking.`
            : `${report.includedCount} order${report.includedCount === 1 ? '' : 's'} ready.`);
        this.showStatus(message, 100, report.blockers.length ? 'warning' : (report.reviewCount ? 'warning' : 'success'));
        this.elements.review.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        this.state.preflightChecking = false;
        console.error(error);
        this.showError(error?.message || 'The PDFs could not be analysed.');
      } finally {
        this.setBusy(false);
      }
    }

    async openMatchPreview(shippingPage) {
      const match = this.state.matches.find((item) => item.shippingPage === Number(shippingPage));
      if (!match || match.packingPage == null || !this.elements.matchPreviewModal) return;

      const shippingRecord = this.state.shippingRecords[match.shippingPage];
      const packingRecord = this.state.packingRecords[match.packingPage];
      const report = this.getPreflightReport();
      const issues = this.getMatchPreflightIssues(match, report);

      this.elements.matchPreviewTitle.textContent = `Review match for label ${match.shippingPage + 1}`;
      this.elements.matchPreviewReason.textContent = issues.length
        ? `Why it was flagged: ${issues.join(' · ')}`
        : 'Compare the two source pages below before printing.';
      this.elements.matchPreviewShippingTitle.textContent = `Shipping label · Page ${match.shippingPage + 1}`;
      this.elements.matchPreviewPackingTitle.textContent = `Packing slip · Page ${match.packingPage + 1}`;
      this.elements.matchPreviewShipping.innerHTML = '<div class="ail-match-preview-loading">Loading shipping label…</div>';
      this.elements.matchPreviewPacking.innerHTML = '<div class="ail-match-preview-loading">Loading packing slip…</div>';
      this.elements.matchPreviewModal.hidden = false;
      this.elements.matchPreviewModal.setAttribute('aria-hidden', 'false');

      try {
        const [shippingCanvas, packingCanvas] = await Promise.all([
          renderPdfPage(this.state.shippingPdf, match.shippingPage, 900, 2.2),
          renderPdfPage(this.state.packingPdf, match.packingPage, 900, 2.2)
        ]);
        shippingCanvas.className = 'ail-match-preview-canvas';
        packingCanvas.className = 'ail-match-preview-canvas';
        this.elements.matchPreviewShipping.innerHTML = '';
        this.elements.matchPreviewPacking.innerHTML = '';
        this.elements.matchPreviewShipping.appendChild(shippingCanvas);
        this.elements.matchPreviewPacking.appendChild(packingCanvas);
      } catch (error) {
        console.error('Could not render match preview.', error);
        this.elements.matchPreviewShipping.innerHTML = '<div class="ail-match-preview-error">Preview could not be loaded.</div>';
        this.elements.matchPreviewPacking.innerHTML = '<div class="ail-match-preview-error">Preview could not be loaded.</div>';
      }
    }

    closeMatchPreview() {
      if (!this.elements.matchPreviewModal) return;
      this.elements.matchPreviewModal.hidden = true;
      this.elements.matchPreviewModal.setAttribute('aria-hidden', 'true');
      if (this.elements.matchPreviewShipping) this.elements.matchPreviewShipping.innerHTML = '';
      if (this.elements.matchPreviewPacking) this.elements.matchPreviewPacking.innerHTML = '';
    }

    renderMatches() {
      const packingOptions = this.state.packingRecords;
      const report = this.getPreflightReport();
      this.elements.matchBody.innerHTML = this.state.matches.map((match) => {
        const shipping = this.state.shippingRecords[match.shippingPage];
        const isExcluded = Boolean(match.excluded);
        const issues = this.getMatchPreflightIssues(match, report);
        const hasProblem = !isExcluded && issues.length > 0;
        const options = [
          '<option value="">-- Unmatched --</option>',
          ...packingOptions.map((packing) => {
            const selected = packing.pageIndex === match.packingPage ? ' selected' : '';
            return `<option value="${packing.pageIndex}"${selected}>Page ${packing.pageIndex + 1}: ${escapeHtml(displayIdentity(packing))}</option>`;
          })
        ].join('');
        const statusLabel = isExcluded
          ? 'Removed'
          : (match.packingPage == null ? 'Needs match' : (issues.length ? 'Check' : 'Ready'));
        const statusClass = isExcluded
          ? 'removed'
          : (match.packingPage == null ? 'problem' : (issues.length ? 'review' : 'ready'));
        const statusNote = isExcluded
          ? 'Print manually'
          : (issues.length ? escapeHtml(issues.join(' · ')) : 'Matched');
        const technicalTitle = `Matched by ${match.method || 'Unknown'} · ${Math.round(match.score || 0)}% · ${match.confidence || 'Unknown'} confidence`;
        return `
          <tr data-confidence="${match.confidence}" data-excluded="${isExcluded ? 'true' : 'false'}" data-preflight-problem="${hasProblem ? 'true' : 'false'}" data-shipping-page="${match.shippingPage}">
            <td><strong>${match.shippingPage + 1}</strong></td>
            <td><div class="ail-match-key">${escapeHtml(displayIdentity(shipping))}</div></td>
            <td><select class="ail-select" data-role="packing-select" data-shipping-page="${match.shippingPage}"${isExcluded ? ' disabled' : ''}>${options}</select></td>
            <td title="${escapeHtml(technicalTitle)}">
              <span class="ail-simple-status ail-simple-status--${statusClass}">${statusLabel}</span>
              <span class="ail-simple-status-note">${statusNote}</span>
              ${(!isExcluded && issues.length && match.packingPage != null)
                ? `<button class="ail-match-preview-button" data-role="view-match-pages" data-shipping-page="${match.shippingPage}" type="button">View matched pages</button>`
                : ''}
            </td>
            <td class="ail-batch-cell">
              <button class="ail-row-action${isExcluded ? ' ail-row-action--restore' : ''}" data-role="toggle-order" data-shipping-page="${match.shippingPage}" type="button">
                ${isExcluded ? 'Restore' : 'Remove'}
              </button>
            </td>
          </tr>`;
      }).join('');

      this.elements.matchBody.querySelectorAll('[data-role="packing-select"]').forEach((select) => {
        select.addEventListener('change', async () => {
          const shippingPage = Number(select.dataset.shippingPage);
          const match = this.state.matches.find((item) => item.shippingPage === shippingPage);
          if (!match) return;
          match.packingPage = select.value === '' ? null : Number(select.value);
          match.method = 'Manual selection';
          match.score = match.packingPage == null ? 0 : 100;
          match.confidence = match.packingPage == null ? 'Low' : 'High';
          this.invalidateGeneratedPreview();

          const needsSafetyCheck = match.packingPage != null && !this.state.packingSafety?.[match.packingPage];
          this.state.preflightChecking = Boolean(needsSafetyCheck);
          this.renderMatches();

          if (needsSafetyCheck) {
            this.setBusy(true);
            try {
              await this.validatePackingSafety([match.packingPage]);
              const updatedReport = this.getPreflightReport();
              this.showStatus(
                updatedReport.ready
                  ? 'Manual match checked and the batch is ready to generate.'
                  : 'Manual match updated. Check the order highlighted below.',
                100,
                updatedReport.ready ? 'success' : 'warning'
              );
            } finally {
              this.state.preflightChecking = false;
              this.setBusy(false);
              this.renderMatches();
            }
          }
        });
      });

      this.elements.matchBody.querySelectorAll('[data-role="view-match-pages"]').forEach((button) => {
        button.addEventListener('click', () => this.openMatchPreview(Number(button.dataset.shippingPage)));
      });

      this.elements.matchBody.querySelectorAll('[data-role="toggle-order"]').forEach((button) => {
        button.addEventListener('click', () => {
          const shippingPage = Number(button.dataset.shippingPage);
          const match = this.state.matches.find((item) => item.shippingPage === shippingPage);
          if (!match) return;
          match.excluded = !match.excluded;
          this.invalidateGeneratedPreview();
          this.renderMatches();
        });
      });

      if (this.elements.batchSummary) {
        const removed = this.state.matches.filter((match) => match.excluded).length;
        const included = this.state.matches.length - removed;
        this.elements.batchSummary.textContent = `${included} of ${this.state.matches.length} order${this.state.matches.length === 1 ? '' : 's'} included in this batch${removed ? ` · ${removed} removed for manual printing` : ''}.`;
      }

      this.renderPreflight();
      this.applyMatchFilter();
    }


    getUsageStorageKey() {
      const scope = String(this.root?.dataset?.usageScope || 'default').replace(/[^a-z0-9_-]/gi, '').slice(0, 80) || 'default';
      return `detailgear_milg_usage_${scope}_v1`;
    }

    loadUsage() {
      try {
        const raw = window.localStorage?.getItem(this.getUsageStorageKey());
        this.state.usage = normaliseUsageData(raw ? JSON.parse(raw) : null);
      } catch (error) {
        console.warn('Usage dashboard storage could not be read.', error);
        this.state.usage = normaliseUsageData(this.state.usage);
      }
      return this.state.usage;
    }

    saveUsage() {
      try {
        window.localStorage?.setItem(this.getUsageStorageKey(), JSON.stringify(normaliseUsageData(this.state.usage)));
      } catch (error) {
        console.warn('Usage dashboard storage could not be saved.', error);
      }
    }

    recordSuccessfulUsage(orderCount) {
      this.state.usage = recordUsageBatch(this.state.usage, orderCount, new Date());
      this.saveUsage();
      this.renderUsageDashboard();
    }

    renderUsageDashboard() {
      if (!this.elements?.usageDashboard) return;
      const usage = normaliseUsageData(this.state.usage);
      const month = usage.months[usageMonthKey(new Date())] || { orders: 0, batches: 0 };
      const format = (value) => new Intl.NumberFormat('en-GB').format(Math.max(0, Number(value) || 0));
      if (this.elements.usageMonthOrders) this.elements.usageMonthOrders.textContent = format(month.orders);
      if (this.elements.usageMonthBatches) this.elements.usageMonthBatches.textContent = format(month.batches);
      if (this.elements.usageTotalOrders) this.elements.usageTotalOrders.textContent = format(usage.totalOrders);
    }

    getLayout() {
      const number = (element, fallback) => {
        const value = Number(element.value);
        return Number.isFinite(value) ? value : fallback;
      };
      return {
        labelX: number(this.elements.labelX, 10),
        labelY: number(this.elements.labelY, 10),
        labelWidth: number(this.elements.labelWidth, 100),
        labelHeight: number(this.elements.labelHeight, 150),
        packingX: number(this.elements.packingX, 10),
        packingY: number(this.elements.packingY, 165),
        packingWidth: number(this.elements.packingWidth, 190),
        packingHeight: number(this.elements.packingHeight, 122)
      };
    }

    validateLayout(layout, paperProfile = this.getPaperProfile()) {
      const fields = Object.values(layout);
      if (fields.some((value) => !Number.isFinite(value) || value < 0)) throw new Error('Layout measurements must be valid positive numbers.');
      const pageWidthMm = paperProfile?.pageWidthMm || 210;
      const pageHeightMm = paperProfile?.pageHeightMm || 297;
      if (layout.labelX + layout.labelWidth > pageWidthMm || layout.labelY + layout.labelHeight > pageHeightMm) throw new Error('The shipping-label position extends beyond the selected page size.');
      if (layout.packingX + layout.packingWidth > pageWidthMm || layout.packingY + layout.packingHeight > pageHeightMm) throw new Error('The packing-slip position extends beyond the selected page size.');
    }

    async generate() {
      if (this.state.isBusy) return;
      const preflight = this.getPreflightReport();
      if (!preflight.ready) {
        const firstIssue = preflight.blockers[0]?.message || 'The batch has not passed the pre-flight checks.';
        this.showError(`${firstIssue} Review the Batch pre-flight panel before creating the PDF.`);
        this.elements.review.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      const unresolvedIncluded = this.state.matches.filter((match) => !match.excluded && match.packingPage == null);
      if (unresolvedIncluded.length) {
        this.showError(`There ${unresolvedIncluded.length === 1 ? 'is' : 'are'} ${unresolvedIncluded.length} included order${unresolvedIncluded.length === 1 ? '' : 's'} without a packing-slip match. Match ${unresolvedIncluded.length === 1 ? 'it' : 'them'} or click Remove to leave ${unresolvedIncluded.length === 1 ? 'it' : 'them'} out and print manually.`);
        this.elements.review.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const selectedMatches = this.state.matches.filter((match) => !match.excluded && match.packingPage != null);
      if (!selectedMatches.length) {
        this.showError('Please include and match at least one order before creating the integrated-label PDF.');
        return;
      }

      const duplicatePacking = selectedMatches
        .map((match) => match.packingPage)
        .filter((page, index, all) => all.indexOf(page) !== index);
      if (duplicatePacking.length && !window.confirm('The same packing-slip page has been selected more than once. Continue anyway?')) return;

      const channelProfile = this.getSalesChannelProfile();
      const courierProfile = this.getCourierProfile();
      const paperProfile = this.getPaperProfile();
      // Version 1.7 enforces product-summary-only output. The full customer
      // address block is never copied into the disposable packing section.
      const useProductSummary = true;

      this.setBusy(true);
      this.showGenerationProgress('Preparing integrated-label PDF...', 2, 'info');
      try {
        const layout = this.getLayout();
        this.validateLayout(layout, paperProfile);
        const pageWidthPt = (paperProfile.pageWidthMm || 210) * MM_TO_PT;
        const pageHeightPt = (paperProfile.pageHeightMm || 297) * MM_TO_PT;
        const isThermalSeparate = paperProfile.outputMode === 'thermal_separate';
        const { PDFDocument } = window.PDFLib;
        const output = await PDFDocument.create();
        // Keep a second pdf-lib copy of the original courier PDF for output.
        // Shipping labels can contain unusual embedded font encodings. Re-rendering
        // those fonts through PDF.js and then rasterising can produce mojibake when a
        // mixed-courier batch contains conflicting font resources. Embedding the
        // original PDF page preserves the carrier's own text/font objects exactly.
        let shippingVectorPdf = null;
        try {
          shippingVectorPdf = await PDFDocument.load(this.state.shippingBytes.slice(), {
            ignoreEncryption: true,
            updateMetadata: false
          });
        } catch (vectorLoadError) {
          console.warn('Vector shipping-label embedding unavailable; using raster fallback.', vectorLoadError);
        }
        output.setTitle(`${channelProfile.label} integrated labels - ${courierProfile.label} - ${paperProfile.label}`);
        output.setSubject('Shipping labels and privacy-safe product summaries');
        output.setCreator('Browser-based Multichannel Integrated Label Generator');
        output.setProducer(`Multichannel Integrated Label Generator ${SCRIPT_VERSION}`);

        let generatedPageCount = 0;
        for (let index = 0; index < selectedMatches.length; index += 1) {
          const match = selectedMatches[index];
          const pageProgress = 5 + ((index / selectedMatches.length) * 88);
          const pageMessage = isThermalSeparate
            ? `Creating order ${index + 1} of ${selectedMatches.length} (label + packing slip)...`
            : `Creating page ${index + 1} of ${selectedMatches.length}...`;
          this.showStatus(pageMessage, pageProgress);
          this.showGenerationProgress(pageMessage, pageProgress, 'info');
          const page = output.addPage([pageWidthPt, pageHeightPt]);
          generatedPageCount += 1;

          const shippingCanvasFull = await renderPdfPage(this.state.shippingPdf, match.shippingPage, 1650);
          const shippingBounds = this.elements.autoCrop.checked
            ? findInkBounds(shippingCanvasFull)
            : { x: 0, y: 0, width: shippingCanvasFull.width, height: shippingCanvasFull.height };
          // Prefer embedding the original courier page as vector PDF content.
          // This avoids font-encoding corruption in mixed-courier batches (for
          // example a Yodel page accidentally included with EVRi labels). The
          // rendered canvas is still used to find the printable crop bounds.
          let shippingDrawn = false;
          if (shippingVectorPdf) {
            try {
              await drawShippingPageVector({
                output,
                outputPage: page,
                sourcePdf: shippingVectorPdf,
                sourcePageIndex: match.shippingPage,
                sourceCanvas: shippingCanvasFull,
                bounds: shippingBounds,
                layout,
                paperProfile
              });
              shippingDrawn = true;
            } catch (vectorDrawError) {
              console.warn(`Vector embedding failed for shipping page ${match.shippingPage + 1}; using raster fallback.`, vectorDrawError);
            }
          }

          if (!shippingDrawn) {
            let shippingCanvas = cropCanvas(
              shippingCanvasFull,
              shippingBounds.x,
              shippingBounds.y,
              shippingBounds.width,
              shippingBounds.height
            );
            shippingCanvas = orientShippingCanvasForPaper(shippingCanvas, layout, paperProfile);
            const shippingBytes = await canvasToBytes(shippingCanvas, 'image/png');
            const shippingImage = await output.embedPng(shippingBytes);
            const shippingBox = {
              x: layout.labelX * MM_TO_PT,
              y: page.getHeight() - ((layout.labelY + layout.labelHeight) * MM_TO_PT),
              width: layout.labelWidth * MM_TO_PT,
              height: layout.labelHeight * MM_TO_PT
            };
            const shippingPlacement = fitRect(
              shippingImage.width,
              shippingImage.height,
              shippingBox.x,
              shippingBox.y,
              shippingBox.width,
              shippingBox.height
            );
            page.drawImage(shippingImage, shippingPlacement);
          }

          const packingCanvasFull = await renderPdfPage(this.state.packingPdf, match.packingPage, 1550);
          const packingLayoutBoxes = useProductSummary
            ? await this.ensurePackingLayoutOcr(match.packingPage, packingCanvasFull)
            : (this.state.packingRecords[match.packingPage]?.ocrBoxes || []);
          await emphasiseMultiItemQuantities(
            this.state.packingPdf,
            match.packingPage,
            packingCanvasFull,
            packingLayoutBoxes,
            channelProfile.id
          );
          let packingCanvas;
          const shippingRecord = this.state.shippingRecords[match.shippingPage];
          const packingRecord = this.state.packingRecords[match.packingPage];
          // Only show the postcode-check banner when the packing slip itself
          // contains a readable postcode. Marketplace slips with no recipient
          // address (for example many TikTok and Temu slips) therefore show the
          // product summary only, even when the courier label contains an address.
          const showPostcodeHeader = packingSlipHasPostcode(packingRecord);
          const postcode = showPostcodeHeader
            ? selectDisplayPostcode(shippingRecord, packingRecord)
            : '';
          if (useProductSummary) {
            // Strict privacy mode: only the item table, totals and the separately
            // printed postcode are copied. The customer name, street address,
            // town/county, buyer name and order-header address block never enter
            // the output image.
            const productCanvas = await createProductOnlyPackingCanvas(
              this.state.packingPdf,
              match.packingPage,
              packingCanvasFull,
              packingLayoutBoxes,
              channelProfile.id
            );
            packingCanvas = showPostcodeHeader
              ? addPostcodeHeaderToCanvas(productCanvas, postcode)
              : productCanvas;
          } else {
            // Retained internally for backwards compatibility, but the Shopify
            // interface no longer exposes this less-private mode.
            await redactPackingCanvas(
              this.state.packingPdf,
              match.packingPage,
              packingCanvasFull,
              packingLayoutBoxes
            );
            const packingBounds = this.elements.autoCrop.checked
              ? findInkBounds(packingCanvasFull)
              : { x: 0, y: 0, width: packingCanvasFull.width, height: packingCanvasFull.height };
            packingCanvas = cropCanvas(
              packingCanvasFull,
              packingBounds.x,
              packingBounds.y,
              packingBounds.width,
              packingBounds.height
            );
            if (showPostcodeHeader) {
              packingCanvas = addPostcodeHeaderToCanvas(packingCanvas, postcode);
            }
          }
          const packingBox = {
            x: layout.packingX * MM_TO_PT,
            y: pageHeightPt - ((layout.packingY + layout.packingHeight) * MM_TO_PT),
            width: layout.packingWidth * MM_TO_PT,
            height: layout.packingHeight * MM_TO_PT
          };

          if (isThermalSeparate) {
            // Zebra/thermal output keeps each order together: portrait shipping
            // label first, then a landscape packing slip using the same physical
            // 6 x 4 in media. Landscape gives the product table roughly 58% more
            // usable width than the original portrait packing page, making the
            // writing materially larger. Long orders are split before scaling
            // would make the text smaller, with continuation pages inserted before
            // the next order's shipping label.
            const thermalPackingMarginMm = 3;
            const thermalPackingPageWidthMm = paperProfile.pageHeightMm || 152.4;
            const thermalPackingPageHeightMm = paperProfile.pageWidthMm || 101.6;
            const thermalPackingBoxMm = {
              x: thermalPackingMarginMm,
              y: thermalPackingMarginMm,
              width: thermalPackingPageWidthMm - (thermalPackingMarginMm * 2),
              height: thermalPackingPageHeightMm - (thermalPackingMarginMm * 2)
            };
            const packingChunks = splitPackingCanvasForThermalOutput(
              packingCanvas,
              thermalPackingBoxMm.width,
              thermalPackingBoxMm.height
            );

            const thermalPackingPageWidthPt = thermalPackingPageWidthMm * MM_TO_PT;
            const thermalPackingPageHeightPt = thermalPackingPageHeightMm * MM_TO_PT;
            const thermalPackingBox = {
              x: thermalPackingBoxMm.x * MM_TO_PT,
              y: thermalPackingBoxMm.y * MM_TO_PT,
              width: thermalPackingBoxMm.width * MM_TO_PT,
              height: thermalPackingBoxMm.height * MM_TO_PT
            };

            for (let chunkIndex = 0; chunkIndex < packingChunks.length; chunkIndex += 1) {
              const packingPage = output.addPage([thermalPackingPageWidthPt, thermalPackingPageHeightPt]);
              generatedPageCount += 1;
              const chunkBytes = await canvasToBytes(packingChunks[chunkIndex], 'image/png');
              const chunkImage = await output.embedPng(chunkBytes);
              const placement = fitRect(
                chunkImage.width,
                chunkImage.height,
                thermalPackingBox.x,
                thermalPackingBox.y,
                thermalPackingBox.width,
                thermalPackingBox.height
              );
              packingPage.drawImage(chunkImage, placement);

              // A small page marker is useful for multi-page picks without
              // competing with the marketplace product information.
              if (packingChunks.length > 1) {
                packingPage.drawText(`PACKING SLIP ${chunkIndex + 1}/${packingChunks.length}`, {
                  x: thermalPackingBox.x,
                  y: 0.7 * MM_TO_PT,
                  size: 5.5
                });
              }

              const thermalProgress = Math.min(94, pageProgress + (((chunkIndex + 1) / Math.max(1, packingChunks.length)) * 2));
              this.showGenerationProgress(
                packingChunks.length > 1
                  ? `Adding large-text packing-slip page ${chunkIndex + 1} of ${packingChunks.length} for order ${index + 1}...`
                  : `Adding large-text packing slip for order ${index + 1}...`,
                thermalProgress,
                'info'
              );
            }
          } else {
            // Long multi-SKU orders are never silently truncated. Keep a single
            // integrated page when the complete summary remains readable; when it
            // would have to shrink too far, split at a table-row boundary and add
            // one or more privacy-safe continuation pages.
            const packingChunks = splitPackingCanvasForOutput(packingCanvas, layout);
            const hasContinuation = packingChunks.length > 1;
            const firstChunkBytes = await canvasToBytes(packingChunks[0], 'image/png');
            const firstChunkImage = await output.embedPng(firstChunkBytes);
            const firstPackingBox = hasContinuation
              ? {
                  x: packingBox.x,
                  y: packingBox.y + (9 * MM_TO_PT),
                  width: packingBox.width,
                  height: Math.max(10, packingBox.height - (9 * MM_TO_PT))
                }
              : packingBox;
            const firstPlacement = fitRect(
              firstChunkImage.width,
              firstChunkImage.height,
              firstPackingBox.x,
              firstPackingBox.y,
              firstPackingBox.width,
              firstPackingBox.height
            );
            page.drawImage(firstChunkImage, firstPlacement);
            if (hasContinuation) drawContinuationNote(page, packingBox);

            if (hasContinuation) {
              const orderReference = (
                packingRecord?.orderIds?.[0] ||
                shippingRecord?.orderIds?.[0] ||
                packingRecord?.trackingIds?.[0] ||
                shippingRecord?.trackingIds?.[0] ||
                `shipping-label-page-${match.shippingPage + 1}`
              );

              for (let chunkIndex = 1; chunkIndex < packingChunks.length; chunkIndex += 1) {
                const continuationPage = output.addPage([A4_WIDTH_PT, A4_HEIGHT_PT]);
                generatedPageCount += 1;
                const continuationNumber = chunkIndex + 1;
                continuationPage.drawText('PRODUCTS CONTINUED', {
                  x: 10 * MM_TO_PT,
                  y: A4_HEIGHT_PT - (16 * MM_TO_PT),
                  size: 18
                });
                continuationPage.drawText(`Order reference: ${String(orderReference).slice(0, 70)}`, {
                  x: 10 * MM_TO_PT,
                  y: A4_HEIGHT_PT - (24 * MM_TO_PT),
                  size: 10
                });

                const moreAfter = chunkIndex < packingChunks.length - 1;
                const continuationBox = {
                  x: 10 * MM_TO_PT,
                  y: (moreAfter ? 18 : 10) * MM_TO_PT,
                  width: 190 * MM_TO_PT,
                  height: (moreAfter ? 247 : 255) * MM_TO_PT
                };
                const continuationBytes = await canvasToBytes(packingChunks[chunkIndex], 'image/png');
                const continuationImage = await output.embedPng(continuationBytes);
                const continuationPlacement = fitRect(
                  continuationImage.width,
                  continuationImage.height,
                  continuationBox.x,
                  continuationBox.y,
                  continuationBox.width,
                  continuationBox.height
                );
                continuationPage.drawImage(continuationImage, continuationPlacement);
                if (moreAfter) {
                  drawContinuationNote(continuationPage, {
                    x: 10 * MM_TO_PT,
                    y: 7 * MM_TO_PT,
                    width: 190 * MM_TO_PT,
                    height: 9 * MM_TO_PT
                  });
                }

                const continuationProgress = Math.min(94, pageProgress + ((continuationNumber / packingChunks.length) * 2));
                this.showGenerationProgress(
                  `Adding product continuation page ${continuationNumber} of ${packingChunks.length} for order ${index + 1}...`,
                  continuationProgress,
                  'info'
                );
              }
            }
          }
        }

        this.showStatus('Finalising and optimising the PDF...', 96);
        this.showGenerationProgress('Finalising and optimising the PDF...', 96, 'info');
        const pdfBytes = await output.save({ useObjectStreams: true });
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        if (this.state.downloadUrl) URL.revokeObjectURL(this.state.downloadUrl);
        this.state.downloadUrl = URL.createObjectURL(blob);
        const date = new Date().toISOString().slice(0, 10);
        this.elements.download.href = this.state.downloadUrl;
        const fileChannel = channelProfile.id.replaceAll('_', '-');
        const fileCourier = courierProfile.id.replaceAll('_', '-');
        const filePaper = paperProfile.id.replaceAll('_', '-');
        this.elements.download.download = `${fileChannel}-${fileCourier}-${filePaper}-integrated-labels-${date}.pdf`;
        this.elements.previewFrame.src = this.state.downloadUrl;
        this.elements.preview.hidden = false;
        const minimumPages = isThermalSeparate ? selectedMatches.length * 2 : selectedMatches.length;
        const continuationCount = Math.max(0, generatedPageCount - minimumPages);
        const removedCount = this.state.matches.filter((match) => match.excluded).length;
        const baseFinishedMessage = isThermalSeparate
          ? `Finished. ${selectedMatches.length} order${selectedMatches.length === 1 ? '' : 's'} created as ${generatedPageCount} 6 × 4 in thermal page${generatedPageCount === 1 ? '' : 's'} in label-then-large-text-packing-slip order${continuationCount ? `, including ${continuationCount} extra packing continuation page${continuationCount === 1 ? '' : 's'}` : ''}.`
          : (continuationCount
            ? `Finished. ${selectedMatches.length} integrated-label page${selectedMatches.length === 1 ? '' : 's'} plus ${continuationCount} product continuation page${continuationCount === 1 ? '' : 's'} created.`
            : `Finished. ${selectedMatches.length} integrated-label page${selectedMatches.length === 1 ? '' : 's'} created.`);
        const finishedMessage = removedCount
          ? `${baseFinishedMessage} ${removedCount} removed order${removedCount === 1 ? '' : 's'} ${removedCount === 1 ? 'was' : 'were'} left out for manual printing.`
          : baseFinishedMessage;
        this.recordSuccessfulUsage(selectedMatches.length);
        this.showStatus(finishedMessage, 100, 'success');
        this.showGenerationProgress('PDF ready to preview and download.', 100, 'success');
        this.elements.preview.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (error) {
        console.error(error);
        const message = error?.message || 'The combined PDF could not be created.';
        this.showError(message);
        this.showGenerationProgress(message, 100, 'error');
      } finally {
        this.setBusy(false);
      }
    }

    async reset() {
      if (this.state.ocrWorker) {
        await this.state.ocrWorker.terminate().catch(() => {});
      }
      if (this.state.downloadUrl) URL.revokeObjectURL(this.state.downloadUrl);
      const retainedUsage = normaliseUsageData(this.state.usage);
      this.state = {
        shippingFile: null,
        packingFile: null,
        shippingBytes: null,
        packingBytes: null,
        shippingPdf: null,
        packingPdf: null,
        shippingRecords: [],
        packingRecords: [],
        matches: [],
        packingSafety: {},
        preflightFilter: 'all',
        preflightChecking: false,
        detectedCourierId: '',
        detectedSalesChannelId: '',
        detectionComplete: false,
        shippingDetectionStatus: 'idle',
        packingDetectionStatus: 'idle',
        shippingDetectionToken: 0,
        packingDetectionToken: 0,
        ocrWorker: null,
        downloadUrl: null,
        isBusy: false,
        usage: retainedUsage
      };
      this.elements.shippingInput.value = '';
      this.elements.packingInput.value = '';
      this.elements.status.hidden = true;
      this.elements.progress.value = 0;
      this.resetGenerationProgress();
      this.elements.matchBody.innerHTML = '';
      this.hideReview();
      this.updateProfileUi();
      this.updateDetectionUi();
      this.updateFileSummaries();
      this.renderUsageDashboard();
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  window.MultichannelIntegratedLabelGeneratorCore = Object.freeze({
    version: SCRIPT_VERSION,
    usageMonthKey,
    normaliseUsageData,
    recordUsageBatch,
    parseIdentity,
    displayIdentity,
    extractOrderIds,
    amazonOrderDigits,
    findNearAmazonOrderMatch,
    reconcileAmazonEvriOcrOrderIds,
    extractChannelOrderIds,
    normaliseTemuOrderId,
    normaliseTrackingId,
    extractTrackingIds,
    extractPostcodes,
    extractAmazonShippingRecipient,
    parseQuantityValue,
    quantityDisplayText,
    isPackingTableHeader,
    isAmazonItemsCountHeader,
    isPublicPackingFooterText,
    isExplicitProductHeaderRow,
    findPackingTableHeader,
    findProductOnlyPackingHeader,
    courierProfiles: COURIER_PROFILES,
    salesChannelProfiles: SALES_CHANNEL_PROFILES,
    paperProfiles: PAPER_PROFILES,
    shouldRotateForBox,
    fittedWidthFraction,
    selectDisplayPostcode,
    packingSlipHasPostcode,
    scorePair,
    matchPages,
    duplicateIdentifierGroups,
    duplicatePackingAssignments,
    buildPreflightReport,
    detectCourierFromText,
    detectSalesChannelFromText,
    chooseDetectedProfile
  });

  function initialise() {
    document.querySelectorAll('[data-multichannel-integrated-label-generator]').forEach((root) => {
      if (root.dataset.initialised === 'true') return;
      root.dataset.initialised = 'true';
      try {
        new IntegratedLabelTool(root);
      } catch (error) {
        console.error('Could not initialise Integrated Label Tool', error);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise, { once: true });
  else initialise();
  document.addEventListener('shopify:section:load', initialise);
})();
